import type { EngineeringBreak } from '../types/breaks';

/**
 * Demo deep-link bases. These point at the public demo tenants so the
 * "Open in {platform}" buttons land somewhere real-looking during onboarding.
 */
const JIRA = 'https://intera-demo.atlassian.net/browse';
const CONFLUENCE = 'https://intera-demo.atlassian.net/wiki/spaces/ENG/pages';
const SLACK = 'https://intera-demo.slack.com/archives';
const GITHUB = 'https://github.com/intera-demo';
const PAGERDUTY = 'https://intera-demo.pagerduty.com/incidents';
const CRM =
  'https://intera-demo.crm.dynamics.com/main.aspx?pagetype=entityrecord';

/**
 * The onboarding gallery dataset - execution breaks Intera surfaced across the
 * engineering org. Mostly playbook breaks (process patterns), a smaller set of
 * commitment breaks (broken promises). A deliberate mix of single-source breaks
 * (one tool was enough) and cross-platform breaks (only correlating several
 * tools revealed the gap).
 */
export const ENGINEERING_BREAKS: EngineeringBreak[] = [
  // ─── Playbook · wrong estimation · cross-platform (the flagship example) ───
  {
    id: 'data-pipeline-reestimated',
    category: 'playbook',
    playbookType: 'wrong-estimation',
    severity: 'high',
    playbook:
      'Any ticket on the shared ingestion pipeline must add the BI / Reporting team as a stakeholder before work starts - they ship on its output.',
    team: 'Data Platform',
    title:
      'Ingestion ticket re-estimated 3×, the reporting team was never told',
    summary:
      'DATA-842 slipped to Sprint 44, but the BI Dashboard epic still expects data by Sprint 42.',
    detail:
      'The ingestion ticket has been re-pointed three times in twelve days (8 → 13 → 21). Each bump pushed delivery further, yet the dependent BI Dashboard epic and the team building it still assume the original Sprint 42 date. Nobody downstream was notified.',
    detectedAgo: '2h ago',
    evidence: [
      {
        platform: 'jira',
        ref: 'DATA-842',
        url: `${JIRA}/DATA-842`,
        quote:
          'Updating story points from 8 to 21. Hit a snag with the Kafka cluster auth. Pushing delivery to Sprint 44 based on the new scope.',
        highlight: 'Pushing delivery to Sprint 44',
        attribution: 'Sarah Chen · yesterday',
      },
      {
        platform: 'slack',
        ref: '#proj-q3-dashboard',
        url: `${SLACK}/C04Q3DASH`,
        quote:
          'Hey team, just confirming we are still on track for the Sprint 42 dashboard release? Leadership is expecting the demo.',
        highlight: 'still on track for the Sprint 42 dashboard release',
        attribution: 'Mark (PM) · 3h ago',
      },
      {
        platform: 'jira',
        ref: 'BI-310 (epic)',
        url: `${JIRA}/BI-310`,
        quote:
          'Epic due date: Sprint 42. Blocked-by: DATA-842. No date change recorded since the re-estimate.',
        attribution: 'unchanged · 11 days',
      },
    ],
    actionPlan: [
      {
        id: 'reschedule-dependents',
        platform: 'jira',
        title: 'Reschedule the 4 dependent BI tickets in Jira',
        detail:
          'Shift the tickets linked to DATA-842 out to Sprint 44 (+10 days).',
        actionLabel: 'Reschedule in Jira',
        appliedLabel: 'Rescheduled 4 tickets',
        gantt: {
          totalUnits: 28,
          groups: [
            { label: 'Sprint 42', sublabel: 'May 19 – 25', start: 0, span: 7 },
            {
              label: 'Sprint 43',
              sublabel: 'May 26 – Jun 1',
              start: 7,
              span: 7,
            },
            { label: 'Sprint 44', sublabel: 'Jun 2 – 8', start: 14, span: 7 },
            { label: 'Sprint 45', sublabel: 'Jun 9 – 15', start: 21, span: 7 },
          ],
          ticks: [
            19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 1, 2, 3, 4, 5,
            6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
          ].map((day, unit) => ({ unit, label: String(day) })),
          todayUnit: 4,
          deadlineUnit: 13,
          deadlineLabel: 'Original deadline',
          impactTiles: [
            { label: 'Original deadline', value: 'Jun 1', tone: 'deadline' },
            { label: 'Now lands', value: 'Jun 11', tone: 'info' },
            { label: 'Slip', value: '+10', unit: 'days', tone: 'warn' },
            { label: 'Dependents', value: '3', tone: 'neutral' },
            { label: 'Bugs traced', value: '2', tone: 'neutral' },
          ],
          narrative:
            '3 dependents across 2 teams are scheduled ahead of the blocker - and Sarah’s own next 2 tickets still sit in Sprint 42 while she’s stuck on DATA-842.',
          narrativeNote: 'Applying realigns dates without changing scope.',
          appliedNarrative:
            'Every dependent now sits behind DATA-842 and Sarah’s own work is rebased to Sprint 44 - all 3 teams are re-baselined.',
          applyLabel: 'Apply & realign 5 tickets',
          appliedLabel: 'Realigned 5 tickets',
          tasks: [
            {
              ref: 'DATA-842',
              description: 'Ingestion ticket re-estimated 3×',
              statusLabel: 'Slipped',
              statusNote: 'Blocker',
              team: 'Data Platform',
              tone: 'slip',
              owner: { name: 'Sarah Chen', initials: 'SC' },
              current: { start: 0, span: 11, label: 'May 19 – May 30' },
              proposed: { start: 3, span: 11, label: 'May 22 – Jun 2' },
            },
            {
              ref: 'DATA-851',
              description: 'Schema validation framework',
              statusLabel: 'Pushed',
              statusNote: 'Sarah at capacity',
              team: 'Data Platform',
              tone: 'cascade',
              owner: { name: 'Sarah Chen', initials: 'SC' },
              current: { start: 6, span: 4, label: 'May 25 – May 28' },
              proposed: { start: 14, span: 4, label: 'Jun 2 – Jun 6' },
            },
            {
              ref: 'DATA-867',
              description: 'Backfill job hardening',
              statusLabel: 'Pushed',
              statusNote: 'Sarah at capacity',
              team: 'Data Platform',
              tone: 'cascade',
              owner: { name: 'Sarah Chen', initials: 'SC' },
              current: { start: 8, span: 3, label: 'May 27 – May 29' },
              proposed: { start: 18, span: 3, label: 'Jun 6 – Jun 9' },
            },
            {
              ref: 'BI-310',
              tag: 'EPIC',
              description: 'Q2 analytics improvements',
              statusLabel: 'Blocked',
              statusNote: 'Blocked by DATA-842',
              team: 'BI / Reporting',
              tone: 'blocked',
              owner: { name: 'James M.', initials: 'JM' },
              current: { start: 2, span: 8, label: 'May 21 – May 29' },
              proposed: { start: 15, span: 8, label: 'Jun 3 – Jun 11' },
              dependsOnBlocker: true,
            },
            {
              ref: 'BI-312',
              description: 'Dashboard – executive KPIs',
              statusLabel: 'Dependent',
              statusNote: 'Stale',
              team: 'BI / Reporting',
              tone: 'dependent',
              owner: { name: 'Nina B.', initials: 'NB' },
              current: { start: 3, span: 5, label: 'May 22 – May 27' },
              proposed: { start: 15, span: 5, label: 'Jun 3 – Jun 8' },
              dependsOnBlocker: true,
            },
            {
              ref: 'BI-340',
              description: 'Data quality alerts',
              statusLabel: 'Dependent',
              statusNote: 'Stale',
              team: 'Analytics',
              tone: 'dependent',
              owner: { name: 'Aman P.', initials: 'AP' },
              current: { start: 4, span: 5, label: 'May 23 – May 28' },
              proposed: { start: 15, span: 5, label: 'Jun 3 – Jun 8' },
              dependsOnBlocker: true,
            },
          ],
          options: [
            {
              id: 'absorb-the-slip',
              name: 'Absorb the slip',
              approach:
                'Move every dependent behind the blocker. No scope cut, no extra cost - the date moves.',
              caveat:
                'The exec demo slips 10 days, from Jun 1 to Jun 11, past the committed date.',
              recommended: true,
              recommendationRationale:
                'Across the last 9 dependency slips on this team, absorbing the date held 100% of scope and shipped a median of 4 days sooner than borrowing or cutting. Reschedules here have stuck - only 1 was re-opened. The team’s velocity has room to carry the +10 days without a follow-on slip.',
              headline: {
                label: 'New delivery',
                value: 'Jun 11 · +10 days',
                tone: 'warn',
              },
              consequences: [
                { label: 'Scope', value: 'Unchanged', tone: 'good' },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Teams moved',
                  value: '3 teams · 5 tickets',
                  tone: 'neutral',
                },
                {
                  label: 'Risk',
                  value: 'Sprint 42 exec demo slips 10 days',
                  tone: 'warn',
                },
              ],
              detail: {
                heading: 'What moves, and by how much',
                summary:
                  'Full scope held · everything re-sequenced behind the blocker',
                items: [
                  {
                    ref: 'DATA-842',
                    title: 'Ingestion pipeline',
                    status: 'Re-baselined',
                    detail:
                      'Lands Jun 2 once the third re-estimate clears. The blocker itself only moves ~2 days - the rest of the slip is the cascade downstream of it.',
                    tone: 'moved',
                  },
                  {
                    ref: 'BI-310',
                    title: 'Q2 analytics improvements',
                    status: 'Moved to Jun 11',
                    detail:
                      'Epic can’t start until DATA-842 lands, so it slides the full 10 days to sit behind the blocker.',
                    tone: 'moved',
                  },
                  {
                    ref: 'BI-312',
                    title: 'Executive KPIs dashboard',
                    status: 'Moved to Jun 8',
                    detail:
                      'Was scheduled against the old ingestion date; re-pointed to the new finish, full scope intact.',
                    tone: 'moved',
                  },
                  {
                    ref: 'BI-340',
                    title: 'Data quality alerts',
                    status: 'Moved to Jun 8',
                    detail:
                      'Same stale-date dependency - re-sequenced behind DATA-842 with the rest of the BI work.',
                    tone: 'moved',
                  },
                  {
                    ref: 'DATA-851',
                    title: 'Schema validation framework',
                    status: 'Moved to Jun 6',
                    detail:
                      'Sarah’s own next ticket; pushed because the slip ate her Sprint 42 capacity, not a finish-to-start block.',
                    tone: 'moved',
                  },
                  {
                    ref: 'DATA-867',
                    title: 'Backfill job hardening',
                    status: 'Moved to Jun 9',
                    detail:
                      'Sarah’s follow-on work, rebased into Sprint 44 behind the schema framework.',
                    tone: 'moved',
                  },
                ],
                trade:
                  'You keep every feature and spend nothing extra - in exchange the exec demo slips from Jun 1 to Jun 11, ten days past the committed date.',
              },
              moves: {},
              impactTiles: [
                {
                  label: 'Original deadline',
                  value: 'Jun 1',
                  tone: 'deadline',
                },
                { label: 'Now lands', value: 'Jun 11', tone: 'info' },
                { label: 'Slip', value: '+10', unit: 'days', tone: 'warn' },
                { label: 'Dependents', value: '3', tone: 'neutral' },
                { label: 'Bugs traced', value: '2', tone: 'neutral' },
              ],
              narrative:
                '3 dependents across 2 teams are scheduled ahead of the blocker - and Sarah’s own next 2 tickets still sit in Sprint 42 while she’s stuck on DATA-842.',
              appliedNarrative:
                'Every dependent now sits behind DATA-842 and Sarah’s own work is rebased to Sprint 44 - all 3 teams are re-baselined, scope intact.',
              applyLabel: 'Absorb & realign 5 tickets',
              appliedLabel: 'Slip absorbed · 5 tickets realigned',
            },
            {
              id: 'protect-the-deadline',
              name: 'Protect the deadline',
              approach:
                'Pair a second engineer onto DATA-842 so the blocker lands on time and the dashboard holds.',
              caveat:
                'Borrows a second engineer for ~2 weeks - Priya’s Search reranking slips with it.',
              headline: {
                label: 'New delivery',
                value: 'Jun 4 · +3 days',
                tone: 'good',
              },
              consequences: [
                { label: 'Scope', value: 'Unchanged', tone: 'good' },
                {
                  label: 'Added cost',
                  value: '1 engineer for ~2 weeks',
                  tone: 'bad',
                },
                {
                  label: 'Side effect',
                  value: 'Priya’s Search reranking slips ~2 weeks',
                  tone: 'warn',
                },
                {
                  label: 'Teams moved',
                  value: '3 teams · 5 tickets re-sequenced',
                  tone: 'neutral',
                },
              ],
              detail: {
                heading: 'How the date holds, and what it costs',
                summary: 'Date nearly held · one engineer borrowed from Search',
                items: [
                  {
                    ref: 'DATA-842',
                    title: 'Ingestion pipeline',
                    status: 'Paired · 2 eng',
                    detail:
                      'Priya pairs with Sarah so the blocker lands May 30 instead of Jun 2 - close enough for the dashboard to hold its date.',
                    tone: 'moved',
                  },
                  {
                    title: 'Priya R. · borrowed',
                    status: 'Out ~2 weeks',
                    detail:
                      'Pulled off Search reranking for roughly two weeks to pair on the blocker. Her own queue is what pays for the saved days.',
                    rationale:
                      'Priya shipped the original ingestion schema DATA-842 builds on, so she ramps on the blocker in hours, not days - the fastest path to landing it on time. Her current ticket (SRCH-204) is also the most deferrable on the team: no downstream dependents this sprint, so borrowing her costs the least elsewhere. Intera ranked her ahead of 2 other Search engineers on context plus availability.',
                    tone: 'added',
                  },
                  {
                    title: 'Search reranking',
                    status: 'Slips ~2 weeks',
                    detail:
                      'Priya’s in-flight work; slips about two weeks while she’s on DATA-842. The slip doesn’t vanish - it moves from the dashboard onto Search.',
                    tone: 'deferred',
                  },
                  {
                    title: '3 dependents (BI-310, BI-312, BI-340)',
                    status: 'Re-sequenced',
                    detail:
                      'The three BI tickets re-point to the May 30 finish and land Jun 4-5, within days of the original date rather than 10 days late.',
                    tone: 'moved',
                  },
                ],
                trade:
                  'You protect the Jun 1 exec demo and keep full scope - the cost is one engineer for ~2 weeks plus a two-week slip pushed onto Search reranking.',
              },
              borrowCandidates: [
                {
                  id: 'priya',
                  recommended: true,
                  engineer: { name: 'Priya R.', initials: 'PR' },
                  source: 'Search reranking',
                  summary:
                    'Out ~2 weeks pairing on DATA-842 - her own queue slips in her place, and one ticket pulls Growth with it.',
                  rationale:
                    'Priya shipped the original ingestion schema DATA-842 builds on, so she ramps on the blocker in hours, not days - the fastest path to landing it on time. Her current ticket (SRCH-204) is also the most deferrable on the team: no downstream dependents this sprint, so borrowing her costs the least elsewhere. Intera ranked her ahead of 2 other Search engineers on context plus availability.',
                  outcome: {
                    label: 'Demo',
                    value: 'Holds Jun 4',
                    tone: 'good',
                  },
                  sideEffect: {
                    label: 'Side effect',
                    value: 'Priya’s Search reranking slips ~2 weeks',
                    tone: 'warn',
                  },
                  landsValue: 'Jun 4',
                  slipValue: '+3',
                  items: [
                    {
                      ref: 'SRCH-204',
                      title: 'Reranking model v2',
                      status: 'Slips ~2 weeks',
                      detail:
                        'Priya’s in-flight work - paused the moment she pairs on the blocker and resumes about two weeks later. This is the slip that moves off the dashboard and onto Search.',
                      tone: 'deferred',
                    },
                    {
                      ref: 'SRCH-211',
                      title: 'Spanish query expansion',
                      status: 'Pushed to Sprint 44',
                      detail:
                        'Her next queued ticket; it can’t start until reranking resumes, so it slides a full sprint behind it.',
                      tone: 'deferred',
                    },
                    {
                      ref: 'GROW-88',
                      title: 'Search ranking A/B test',
                      status: 'Blocked downstream',
                      detail:
                        'Growth’s experiment is blocked-by SRCH-204 - so borrowing Priya doesn’t just delay Search, it ripples one team further and stalls a Growth test for ~2 weeks.',
                      tone: 'moved',
                    },
                  ],
                  addedTask: {
                    ref: 'PAIR-priya',
                    tag: 'BORROWED',
                    description: 'Priya R. paired onto DATA-842',
                    statusLabel: 'Reassigned',
                    statusNote: 'Pulled off Search reranking',
                    team: 'Data Platform',
                    tone: 'cascade',
                    owner: { name: 'Priya R.', initials: 'PR' },
                    proposed: { start: 3, span: 8, label: 'May 22 – May 30' },
                  },
                },
                {
                  id: 'daniel',
                  engineer: { name: 'Daniel K.', initials: 'DK' },
                  source: 'Pipeline observability',
                  summary:
                    'Already on Data Platform, so no cross-team ripple - but he ramps slower and the dashboard lands a week late.',
                  rationale:
                    'Daniel is already on Data Platform, so borrowing him stays in-team - nothing spills onto Search or Growth. But he has never touched the ingestion schema DATA-842 builds on, so Intera estimates 4-5 days of ramp before he is productive on the blocker, pushing the finish to Jun 6 and the dashboard to Jun 11 - past the exec demo. Cheapest on blast radius, most expensive on time.',
                  outcome: {
                    label: 'Demo',
                    value: 'Slips to Jun 11',
                    tone: 'bad',
                  },
                  sideEffect: {
                    label: 'Side effect',
                    value: 'No cross-team slip · cost stays on Data Platform',
                    tone: 'good',
                  },
                  landsValue: 'Jun 11',
                  slipValue: '+10',
                  moves: {
                    'DATA-842': {
                      proposed: {
                        start: 3,
                        span: 13,
                        label: 'May 22 – Jun 6 · 2 eng',
                      },
                      note: 'Ramping on schema',
                    },
                    'DATA-851': {
                      proposed: { start: 16, span: 4, label: 'Jun 6 – Jun 10' },
                    },
                    'DATA-867': {
                      proposed: {
                        start: 20,
                        span: 3,
                        label: 'Jun 10 – Jun 13',
                      },
                    },
                    'BI-310': {
                      proposed: { start: 16, span: 7, label: 'Jun 6 – Jun 12' },
                    },
                    'BI-312': {
                      proposed: { start: 16, span: 5, label: 'Jun 6 – Jun 11' },
                    },
                    'BI-340': {
                      proposed: { start: 16, span: 5, label: 'Jun 6 – Jun 11' },
                    },
                  },
                  items: [
                    {
                      ref: 'DATA-619',
                      title: 'Pipeline observability',
                      status: 'Slips ~1 sprint',
                      detail:
                        'Daniel’s in-flight work - parked the moment he pairs on the blocker and resumes a sprint later. The slip stays inside Data Platform; no other team inherits it.',
                      tone: 'deferred',
                    },
                    {
                      ref: 'DATA-842',
                      title: 'Schema ramp-up',
                      status: '+4-5 days',
                      detail:
                        'He has never worked the ingestion schema, so he needs 4-5 days to get productive before he can move the blocker - that ramp is what pushes the finish from May 30 to Jun 6.',
                      tone: 'partial',
                    },
                    {
                      title: 'Search & Growth',
                      status: 'Untouched',
                      detail:
                        'Unlike borrowing from Search, no second team inherits a slip - the reranking work and the Growth A/B test both stay on track. The entire cost stays on Data Platform.',
                      tone: 'kept',
                    },
                  ],
                  addedTask: {
                    ref: 'PAIR-daniel',
                    tag: 'BORROWED',
                    description: 'Daniel K. paired onto DATA-842',
                    statusLabel: 'Reassigned',
                    statusNote: 'Ramping on ingestion schema',
                    team: 'Data Platform',
                    tone: 'cascade',
                    owner: { name: 'Daniel K.', initials: 'DK' },
                    proposed: { start: 3, span: 13, label: 'May 22 – Jun 6' },
                  },
                },
                {
                  id: 'nadia',
                  engineer: { name: 'Nadia S.', initials: 'NS' },
                  source: 'Enterprise search rollout',
                  summary:
                    'Schema author - lands the blocker fastest, but pulling her risks a committed enterprise rollout.',
                  rationale:
                    'Nadia wrote the ingestion layer itself, so she ramps in minutes and lands the blocker a day ahead of Priya. The cost is the steepest: she is mid-flight on the enterprise search rollout (SRCH-330) a customer is expecting this quarter, so borrowing her puts a signed commitment at risk. Intera ranks her behind Priya only because the rollout slip is harder to absorb than Priya’s reranking slip.',
                  outcome: {
                    label: 'Demo',
                    value: 'Holds Jun 3',
                    tone: 'good',
                  },
                  sideEffect: {
                    label: 'Side effect',
                    value: 'Enterprise rollout SRCH-330 slips ~2 weeks',
                    tone: 'bad',
                  },
                  landsValue: 'Jun 3',
                  slipValue: '+2',
                  moves: {
                    'DATA-842': {
                      proposed: {
                        start: 3,
                        span: 7,
                        label: 'May 22 – May 29 · 2 eng',
                      },
                      note: 'Schema author',
                    },
                  },
                  items: [
                    {
                      ref: 'SRCH-330',
                      title: 'Enterprise search rollout',
                      status: 'Slips ~2 weeks',
                      detail:
                        'A committed customer rollout - pausing it risks a contractual date. This is the priciest queue to borrow from, which is why Intera ranks Nadia behind Priya.',
                      tone: 'deferred',
                    },
                    {
                      ref: 'SRCH-204',
                      title: 'Reranking model v2',
                      status: 'Untouched',
                      detail:
                        'Stays with Priya and ships on time - borrowing Nadia leaves the reranking work alone.',
                      tone: 'kept',
                    },
                    {
                      ref: 'GROW-88',
                      title: 'Search ranking A/B test',
                      status: 'Untouched',
                      detail:
                        'No knock-on to Growth - the ripple Priya’s borrow would cause does not happen here.',
                      tone: 'kept',
                    },
                  ],
                  addedTask: {
                    ref: 'PAIR-nadia',
                    tag: 'BORROWED',
                    description: 'Nadia S. paired onto DATA-842',
                    statusLabel: 'Reassigned',
                    statusNote: 'Pulled off enterprise rollout',
                    team: 'Data Platform',
                    tone: 'cascade',
                    owner: { name: 'Nadia S.', initials: 'NS' },
                    proposed: { start: 3, span: 7, label: 'May 22 – May 29' },
                  },
                },
              ],
              moves: {
                'DATA-842': {
                  proposed: {
                    start: 3,
                    span: 8,
                    label: 'May 22 – May 30 · 2 eng',
                  },
                  note: 'Now 2 engineers',
                },
                'DATA-851': {
                  proposed: { start: 11, span: 4, label: 'May 30 – Jun 3' },
                },
                'DATA-867': {
                  proposed: { start: 15, span: 3, label: 'Jun 3 – Jun 6' },
                },
                'BI-310': {
                  proposed: { start: 11, span: 7, label: 'May 30 – Jun 5' },
                },
                'BI-312': {
                  proposed: { start: 11, span: 5, label: 'May 30 – Jun 4' },
                },
                'BI-340': {
                  proposed: { start: 11, span: 5, label: 'May 30 – Jun 4' },
                },
              },
              impactTiles: [
                {
                  label: 'Original deadline',
                  value: 'Jun 1',
                  tone: 'deadline',
                },
                { label: 'Now lands', value: 'Jun 4', tone: 'info' },
                { label: 'Slip', value: '+3', unit: 'days', tone: 'info' },
                { label: 'Borrowed', value: '1', unit: 'eng', tone: 'warn' },
                { label: 'Dependents', value: '3', tone: 'neutral' },
              ],
              narrative:
                'Pull Priya off Search reranking to pair on DATA-842 - the blocker lands near its original date and the dashboard holds, but Search slips in its place.',
              appliedNarrative:
                'Priya is paired onto DATA-842; the blocker and dashboard land by Jun 4 - the exec demo is protected, at the cost of a two-week Search reranking slip.',
              applyLabel: 'Protect the date · borrow 1 engineer',
              appliedLabel: 'Deadline protected · 1 engineer borrowed',
            },
            {
              id: 'cut-scope-hold-date',
              name: 'Cut scope, hold the date',
              approach:
                'Ship Jun 1 on a reduced dataset: DATA-842 backfills partially, the two BI views (Q2 analytics, exec KPIs) ship partial, and the data-quality alerts defer to Sprint 45.',
              caveat:
                'Two BI views ship partial and alerts defer - the demo runs on reduced data.',
              headline: {
                label: 'Delivery date',
                value: 'Jun 1 · held',
                tone: 'good',
              },
              consequences: [
                {
                  label: 'Scope',
                  value:
                    'BI-310 + BI-312 ship partial · BI-340 alerts deferred',
                  tone: 'bad',
                },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Teams moved',
                  value: '0 · nobody reschedules',
                  tone: 'good',
                },
                {
                  label: 'Risk',
                  value: 'Partial data in demo · backfill debt',
                  tone: 'warn',
                },
              ],
              detail: {
                heading: 'What ships Jun 1, and what doesn’t',
                summary:
                  'Date held · nobody reschedules · demo runs on partial data',
                items: [
                  {
                    ref: 'DATA-842',
                    title: 'Ingestion pipeline',
                    status: 'Ships partial',
                    detail:
                      'Backfills a reduced dataset by May 27 - recent partitions only. The full historical backfill becomes Sprint 45 debt.',
                    tone: 'partial',
                  },
                  {
                    ref: 'BI-310',
                    title: 'Q2 analytics improvements',
                    status: 'Ships partial',
                    detail:
                      'Epic ships against the reduced dataset: trend views render, but anything that needs full history is incomplete.',
                    tone: 'partial',
                  },
                  {
                    ref: 'BI-312',
                    title: 'Executive KPIs dashboard',
                    status: 'Ships partial',
                    detail:
                      'Exec KPIs render on partial data; quarter-over-quarter comparisons stay approximate until the backfill lands.',
                    tone: 'partial',
                  },
                  {
                    ref: 'BI-340',
                    title: 'Data quality alerts',
                    status: 'Deferred to Sprint 45',
                    detail:
                      'Pulled from this release entirely - the demo ships with no automated data-quality alerting for ~2 weeks.',
                    tone: 'deferred',
                  },
                  {
                    ref: 'DATA-851',
                    title: 'Schema validation framework',
                    status: 'Unchanged',
                    detail:
                      'Not on the critical path for the date - ships as originally planned.',
                    tone: 'kept',
                  },
                  {
                    ref: 'DATA-867',
                    title: 'Backfill job hardening',
                    status: 'Unchanged',
                    detail:
                      'Ships as planned; the partial-backfill debt it would harden is exactly what Sprint 45 picks back up.',
                    tone: 'kept',
                  },
                ],
                trade:
                  'You hold Jun 1 and move nobody - in exchange the exec demo runs on a partial dataset, two BI views ship approximate, data-quality alerts go dark for ~2 weeks, and a full backfill becomes Sprint 45 debt.',
              },
              moves: {
                'DATA-842': {
                  proposed: {
                    start: 0,
                    span: 8,
                    label: 'May 19 – May 27 · reduced',
                  },
                  note: 'Reduced scope',
                },
                'DATA-851': { proposed: null, note: 'Unaffected' },
                'DATA-867': { proposed: null, note: 'Unaffected' },
                'BI-310': { proposed: null, note: 'Ships reduced' },
                'BI-312': { proposed: null, note: 'Ships reduced' },
                'BI-340': { dropped: true, note: 'Deferred to Sprint 45' },
              },
              impactTiles: [
                {
                  label: 'Original deadline',
                  value: 'Jun 1',
                  tone: 'deadline',
                },
                { label: 'Ships', value: 'Jun 1', tone: 'info' },
                { label: 'Slip', value: '0', unit: 'days', tone: 'info' },
                { label: 'Features cut', value: '2', tone: 'warn' },
                {
                  label: 'Deferred',
                  value: '1',
                  unit: 'ticket',
                  tone: 'neutral',
                },
              ],
              narrative:
                'Ship the Sprint 42 dashboard on its original date with a reduced dataset - data-quality alerts are deferred and two views ship partial. Nobody reschedules.',
              appliedNarrative:
                'The dashboard ships Jun 1 on a reduced dataset; BI-340 (data-quality alerts) is deferred to Sprint 45 and backfilled later - the date holds, the scope doesn’t.',
              applyLabel: 'Hold the date · cut scope',
              appliedLabel: 'Date held · scope cut',
            },
          ],
        },
        timeline: {
          axis: ['Sprint 42', 'Sprint 43', 'Sprint 44'],
          deadlineIndex: 0,
          delayLabel: '+10 days',
          rows: [
            {
              label: 'DATA-842',
              role: 'Slipped',
              kind: 'slip',
              fromIndex: 0,
              toIndex: 2,
              isPrimary: true,
              note: 're-estimated 3× · 8→13→21',
            },
            {
              label: 'BI-310 epic',
              role: 'Blocked',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 2,
            },
            {
              label: 'BI-312',
              role: 'Blocked',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 2,
            },
            {
              label: 'BI-340',
              role: 'Blocked',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 2,
            },
          ],
          shiftNote:
            'DATA-842 slipped off the Sprint 42 deadline to Sprint 44 - but 3 dependents are still stranded at Sprint 42. This pulls them back behind it.',
          afterNote:
            'Dependents now sit behind DATA-842 in Sprint 44 - nothing is scheduled ahead of its blocker, and the reporting team is re-baselined.',
        },
      },
      {
        id: 'notify-reporting-team',
        platform: 'slack',
        title: 'Send the BI / Reporting team an update on the new dates',
        detail:
          'Post a summary to #proj-q3-dashboard: DATA-842 now lands Sprint 44, demo at risk.',
        actionLabel: 'Send update',
        appliedLabel: 'Update sent to #proj-q3-dashboard',
      },
      {
        id: 'flag-leadership',
        platform: 'calendar',
        title: 'Draft a 15-min alignment sync',
        detail:
          'Propose a slot with the PM and both leads to confirm the new plan.',
        actionLabel: 'Draft invite',
        appliedLabel: 'Invite drafted',
      },
    ],
  },

  // ─── Commitment · cross-platform → no, single-source on Jira dependency ───
  {
    id: 'checkout-epic-blocked',
    category: 'commitment',
    severity: 'high',
    team: 'Checkout',
    title:
      'Checkout launch depends on an Auth ticket that just slipped 2 weeks',
    summary:
      'The Q3 Launch commitment is blocked - Auth Service V2 moved out, the launch sequence did not.',
    detail:
      'AUTH-1187 (Auth Service V2) was pushed two weeks. The Q3 Checkout Launch is blocked-by that ticket, but its target date is unchanged - so the org is still committed to a date that is no longer reachable.',
    detectedAgo: '5h ago',
    evidence: [
      {
        platform: 'jira',
        ref: 'AUTH-1187',
        url: `${JIRA}/AUTH-1187`,
        quote:
          'Re-scoping token rotation. New target: +2 weeks. This blocks CHECKOUT-900.',
        highlight: 'New target: +2 weeks',
        attribution: 'David Park · today',
      },
    ],
    actionPlan: [
      {
        id: 'recompute-launch-date',
        platform: 'jira',
        title: 'Recompute the Checkout Launch date from the critical path',
        detail: 'Roll the +2 week slip on AUTH-1187 up to CHECKOUT-900.',
        actionLabel: 'Recalculate',
        appliedLabel: 'Launch date moved to Aug 28',
        gantt: {
          totalUnits: 21,
          groups: [
            { label: 'Aug 14', sublabel: 'committed', start: 0, span: 7 },
            { label: 'Aug 21', start: 7, span: 7 },
            { label: 'Aug 28', sublabel: 'critical path', start: 14, span: 7 },
          ],
          ticks: [
            { unit: 0, label: '14' },
            { unit: 7, label: '21' },
            { unit: 14, label: '28' },
          ],
          todayUnit: 2,
          deadlineUnit: 0,
          deadlineLabel: 'Committed date',
          impactTiles: [
            { label: 'Committed date', value: 'Aug 14', tone: 'deadline' },
            { label: 'Now reachable', value: 'Aug 28', tone: 'info' },
            { label: 'Slip', value: '+2', unit: 'weeks', tone: 'warn' },
            { label: 'Commitment', value: 'Q3 launch', tone: 'neutral' },
          ],
          narrative:
            'The launch is still committed to Aug 14, but the blocker it depends on now lands Aug 28.',
          narrativeNote:
            'Roll the slip up to the launch and notify stakeholders.',
          appliedNarrative:
            'Checkout launch re-baselined to Aug 28, and David’s MFA work is rebased behind the re-scope - the committed date now matches the critical path.',
          applyLabel: 'Recompute launch date',
          appliedLabel: 'Launch moved to Aug 28',
          tasks: [
            {
              ref: 'AUTH-1187',
              description: 'Auth Service V2 · token rotation',
              statusLabel: 'Slipped',
              statusNote: 'Blocker',
              team: 'Identity',
              tone: 'slip',
              owner: { name: 'David Park', initials: 'DP' },
              current: { start: 0, span: 7, label: 'Aug 14 – Aug 21' },
              proposed: { start: 7, span: 7, label: 'Aug 21 – Aug 28' },
            },
            {
              ref: 'AUTH-1192',
              description: 'MFA enrollment polish',
              statusLabel: 'Pushed',
              statusNote: 'David at capacity',
              team: 'Identity',
              tone: 'cascade',
              owner: { name: 'David Park', initials: 'DP' },
              current: { start: 7, span: 4, label: 'Aug 21 – Aug 24' },
              proposed: { start: 16, span: 4, label: 'Aug 28 – Aug 31' },
            },
            {
              ref: 'CHECKOUT-900',
              description: 'Q3 Checkout launch',
              statusLabel: 'Blocked',
              statusNote: 'Blocked by AUTH-1187',
              team: 'Checkout',
              tone: 'blocked',
              owner: { name: 'Mei Lin', initials: 'ML' },
              current: { start: 0, span: 4, label: 'Aug 14 target' },
              proposed: { start: 15, span: 4, label: 'Aug 28 launch' },
              dependsOnBlocker: true,
            },
          ],
          options: [
            {
              id: 'move-the-launch',
              name: 'Move the launch',
              approach:
                'Roll the +2 week slip up to the launch. The committed date moves to match the critical path.',
              caveat:
                'The launch slips 2 weeks, to Aug 28, past the Aug 14 customers were told.',
              recommended: true,
              recommendationRationale:
                'On the last 3 launches with a critical-path slip, moving the date beat compressing scope - every compressed launch shipped a hotfix within 2 weeks, while the moved ones landed clean. Intera estimates the +2 weeks fully clears the blocker with no downstream team taking on overtime.',
              headline: {
                label: 'Launch date',
                value: 'Aug 28 · +2 weeks',
                tone: 'warn',
              },
              consequences: [
                { label: 'Scope', value: 'Unchanged', tone: 'good' },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Stakeholders',
                  value: '2 teams to notify',
                  tone: 'neutral',
                },
                {
                  label: 'Risk',
                  value: 'Customers were told Aug 14',
                  tone: 'warn',
                },
              ],
              detail: {
                heading: 'What moves, and by how much',
                summary:
                  'Full scope held · launch re-based to the critical path',
                items: [
                  {
                    ref: 'AUTH-1187',
                    title: 'Auth Service V2 · token rotation',
                    status: 'Re-based to Aug 28',
                    detail:
                      'The token-rotation re-scope lands Aug 28 instead of Aug 21 - the blocker the whole launch waits on.',
                    tone: 'moved',
                  },
                  {
                    ref: 'CHECKOUT-900',
                    title: 'Q3 Checkout launch',
                    status: 'Moved to Aug 28',
                    detail:
                      'Blocked-by AUTH-1187, so the committed date rolls the full two weeks to match what’s actually reachable.',
                    tone: 'moved',
                  },
                  {
                    ref: 'AUTH-1192',
                    title: 'MFA enrollment polish',
                    status: 'Moved to Aug 31',
                    detail:
                      'David’s follow-on work; pushed behind the re-scope since it shares his capacity.',
                    tone: 'moved',
                  },
                ],
                trade:
                  'You keep full scope and spend nothing - in exchange the launch customers were told was Aug 14 slips two weeks to Aug 28.',
              },
              moves: {},
              impactTiles: [
                { label: 'Committed date', value: 'Aug 14', tone: 'deadline' },
                { label: 'Now reachable', value: 'Aug 28', tone: 'info' },
                { label: 'Slip', value: '+2', unit: 'weeks', tone: 'warn' },
                { label: 'Commitment', value: 'Q3 launch', tone: 'neutral' },
              ],
              narrative:
                'The launch is still committed to Aug 14, but the blocker it depends on now lands Aug 28.',
              appliedNarrative:
                'Checkout launch re-baselined to Aug 28, and David’s MFA work is rebased behind the rescope - the committed date now matches the critical path.',
              applyLabel: 'Move launch to Aug 28',
              appliedLabel: 'Launch moved to Aug 28',
            },
            {
              id: 'surge-protect-aug-14',
              name: 'Protect Aug 14',
              approach:
                'Surge a second identity engineer onto the token-rotation rescope so the launch holds to Aug 21.',
              caveat:
                'Surges a second engineer off SSO - Sam’s MFA polish slips a week.',
              headline: {
                label: 'Launch date',
                value: 'Aug 21 · +1 week',
                tone: 'good',
              },
              consequences: [
                { label: 'Scope', value: 'Unchanged', tone: 'good' },
                {
                  label: 'Added cost',
                  value: '1 engineer surged',
                  tone: 'bad',
                },
                {
                  label: 'Side effect',
                  value: 'Sam’s MFA polish slips a week',
                  tone: 'warn',
                },
                {
                  label: 'Stakeholders',
                  value: '2 teams to notify',
                  tone: 'neutral',
                },
              ],
              detail: {
                heading: 'How the date holds, and what it costs',
                summary:
                  'Half the slip bought back · one engineer surged from SSO',
                items: [
                  {
                    ref: 'AUTH-1187',
                    title: 'Auth Service V2 · token rotation',
                    status: 'Surged · 2 eng',
                    detail:
                      'Sam pairs on the re-scope so it lands Aug 19 instead of Aug 28 - enough for the launch to hold Aug 21.',
                    tone: 'moved',
                  },
                  {
                    title: 'Sam T. · surged',
                    status: 'Out ~1 week',
                    detail:
                      'Pulled off the SSO backlog for about a week to pair on AUTH-1187. The SSO work waits in his place.',
                    tone: 'added',
                  },
                  {
                    ref: 'CHECKOUT-900',
                    title: 'Q3 Checkout launch',
                    status: 'Lands Aug 21',
                    detail:
                      'Re-points to the Aug 19 finish - one week late instead of two, with full scope.',
                    tone: 'moved',
                  },
                  {
                    ref: 'AUTH-1192',
                    title: 'MFA enrollment polish',
                    status: 'Slips a week',
                    detail:
                      'David’s MFA polish slides to Aug 26-29 to free the identity team for the surge.',
                    tone: 'deferred',
                  },
                ],
                trade:
                  'You cut the slip from two weeks to one and keep full scope - the cost is one borrowed engineer plus a week of slip on the SSO backlog and MFA polish.',
              },
              moves: {
                'AUTH-1187': {
                  proposed: {
                    start: 0,
                    span: 5,
                    label: 'Aug 14 – Aug 19 · 2 eng',
                  },
                  note: 'Surge · 2 engineers',
                },
                'AUTH-1192': {
                  proposed: { start: 12, span: 4, label: 'Aug 26 – Aug 29' },
                },
                'CHECKOUT-900': {
                  proposed: { start: 7, span: 4, label: 'Aug 21 launch' },
                },
              },
              addedTasks: [
                {
                  ref: 'AUTH-surge',
                  tag: 'BORROWED',
                  description: 'Sam T. paired onto AUTH-1187',
                  statusLabel: 'Reassigned',
                  statusNote: 'Pulled off the SSO backlog',
                  team: 'Identity',
                  tone: 'cascade',
                  owner: { name: 'Sam T.', initials: 'ST' },
                  proposed: { start: 0, span: 5, label: 'Aug 14 – Aug 19' },
                },
              ],
              impactTiles: [
                { label: 'Committed date', value: 'Aug 14', tone: 'deadline' },
                { label: 'Now reachable', value: 'Aug 21', tone: 'info' },
                { label: 'Slip', value: '+1', unit: 'week', tone: 'info' },
                { label: 'Surged', value: '1', unit: 'eng', tone: 'warn' },
                { label: 'Commitment', value: 'Q3 launch', tone: 'neutral' },
              ],
              narrative:
                'Surge a second identity engineer onto the token-rotation rescope so AUTH-1187 lands by Aug 19 - the launch holds to Aug 21, one week instead of two.',
              appliedNarrative:
                'AUTH-1187 surged with a paired engineer; the Checkout launch re-baselines to Aug 21 - half the slip, at the cost of one borrowed engineer and a week of MFA polish.',
              applyLabel: 'Protect the date · surge AUTH-1187',
              appliedLabel: 'Date protected · 1 engineer surged',
            },
            {
              id: 'ship-aug-14-defer-rotation',
              name: 'Ship Aug 14, defer rotation',
              approach:
                'Launch Aug 14 behind a flag with manual token handling; ship full rotation as an Aug 28 fast-follow.',
              caveat:
                'Ships on manual token handling behind a flag until the Aug 28 fast-follow.',
              headline: {
                label: 'Launch date',
                value: 'Aug 14 · held',
                tone: 'good',
              },
              consequences: [
                {
                  label: 'Scope',
                  value: 'Two-step launch · rotation deferred',
                  tone: 'bad',
                },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Teams moved',
                  value: '0 · nobody reschedules',
                  tone: 'good',
                },
                {
                  label: 'Risk',
                  value: 'Manual token handling until fast-follow',
                  tone: 'warn',
                },
              ],
              detail: {
                heading: 'What ships Aug 14, and what doesn’t',
                summary: 'Date held · nobody reschedules · two-step rollout',
                items: [
                  {
                    ref: 'CHECKOUT-900',
                    title: 'Q3 Checkout launch',
                    status: 'Ships behind flag',
                    detail:
                      'Launches Aug 14 on schedule, but behind a feature flag with manual token handling until the fast-follow lands.',
                    tone: 'partial',
                  },
                  {
                    ref: 'AUTH-1187',
                    title: 'Auth Service V2 · token rotation',
                    status: 'Minimal · rotation deferred',
                    detail:
                      'Ships a minimal slice Aug 14; full automated token rotation is pulled out of the launch.',
                    tone: 'partial',
                  },
                  {
                    ref: 'AUTH-1187B',
                    title: 'Token rotation · fast-follow',
                    status: 'New · Aug 28',
                    detail:
                      'New fast-follow ticket carrying the deferred rotation; lands Aug 28 - Sep 2, after launch.',
                    tone: 'added',
                  },
                  {
                    ref: 'AUTH-1192',
                    title: 'MFA enrollment polish',
                    status: 'Unchanged',
                    detail:
                      'Not on the launch critical path - ships as originally planned.',
                    tone: 'kept',
                  },
                ],
                trade:
                  'You hold the committed Aug 14 date and move nobody - in exchange the launch runs on manual token handling behind a flag until the Aug 28 fast-follow ships the real rotation.',
              },
              moves: {
                'AUTH-1187': {
                  proposed: { start: 0, span: 4, label: 'Aug 14 · minimal' },
                  note: 'Rotation deferred',
                },
                'AUTH-1192': { proposed: null, note: 'Unaffected' },
                'CHECKOUT-900': { proposed: null, note: 'Ships behind flag' },
              },
              addedTasks: [
                {
                  ref: 'AUTH-1187B',
                  tag: 'NEW',
                  description: 'Token rotation · fast-follow',
                  statusLabel: 'Deferred',
                  statusNote: 'After launch',
                  team: 'Identity',
                  tone: 'cascade',
                  owner: { name: 'David Park', initials: 'DP' },
                  proposed: { start: 14, span: 5, label: 'Aug 28 – Sep 2' },
                },
              ],
              impactTiles: [
                { label: 'Committed date', value: 'Aug 14', tone: 'deadline' },
                { label: 'Ships', value: 'Aug 14', tone: 'info' },
                { label: 'Slip', value: '0', unit: 'weeks', tone: 'info' },
                { label: 'Deferred', value: 'Fast-follow', tone: 'warn' },
                { label: 'Commitment', value: 'Q3 launch', tone: 'neutral' },
              ],
              narrative:
                'Launch Aug 14 behind a feature flag with manual token handling; ship full token rotation as an Aug 28 fast-follow. The committed date holds, the rollout is two-step.',
              appliedNarrative:
                'Checkout launches Aug 14 behind a flag; token rotation lands Aug 28 as a fast-follow - the commitment holds, with a two-step rollout and interim manual handling.',
              applyLabel: 'Hold Aug 14 · defer rotation',
              appliedLabel: 'Date held · rotation deferred',
            },
          ],
        },
        timeline: {
          axis: ['Aug 14', 'Aug 21', 'Aug 28'],
          deadlineIndex: 0,
          delayLabel: '+2 weeks',
          rows: [
            {
              label: 'AUTH-1187',
              role: 'Slipped',
              kind: 'slip',
              fromIndex: 0,
              toIndex: 2,
              isPrimary: true,
              note: 're-scoped · +2 weeks',
            },
            {
              label: 'Checkout Launch',
              role: 'Blocked',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 2,
            },
          ],
          shiftNote:
            'AUTH-1187 slipped +2 weeks off the Aug 14 launch date - but the launch is still committed to Aug 14. Roll the slip up to the launch.',
          afterNote:
            'Checkout Launch re-baselined to Aug 28 - the committed date now matches the critical path.',
        },
      },
      {
        id: 'notify-launch-stakeholders',
        platform: 'slack',
        title: 'Notify launch stakeholders the commitment moved',
        detail: 'Post the revised date and the blocking ticket to #launch-q3.',
        actionLabel: 'Send update',
        appliedLabel: 'Stakeholders notified',
      },
    ],
  },

  // ─── Commitment · cross-platform (the handoff drop) ───
  {
    id: 'prd-employer-number-pathway',
    category: 'commitment',
    severity: 'high',
    team: 'Claims Digitization',
    title:
      'Collective-policy detail was lost in the handoff from PRD to ticket',
    summary:
      'The Motor Claim PRD requires a separate pathway for collective workplace policies, including an employer-number field. The Jira ticket building the eligibility check only validates a policy ID.',
    detail:
      'The Motor Claim PRD calls for a distinct pathway for collective workplace policies, keyed on an employer number. The eligibility-check ticket only validates an individual policy ID - the requirement survived the PRD but not the handoff into the ticket, and nobody downstream knows it was dropped.',
    detectedAgo: '1d ago',
    evidence: [
      {
        platform: 'confluence',
        ref: 'Motor Claim PRD',
        url: 'https://intera-ai-demo.atlassian.net/wiki/spaces/MT/pages/491521/Motor+Claim+PRD',
        quote:
          'In the case of a collective policy through the workplace, a different pathway should be displayed, which will also include a request for the employer number, and it should be ensured that the information is saved and transferred for the continuation of the process.',
        highlight: 'employer number',
        attribution: 'Updated 6 days ago · Ran (DRI)',
      },
      {
        platform: 'jira',
        ref: 'SCRUM-6',
        url: 'https://intera-ai-demo.atlassian.net/browse/SCRUM-6',
        quote:
          'The API should include the following fields: Claim ID, Policy ID, Policy type, Customer ID, Full name, Phone number, Email, Vehicle number, Incident date, Incident time, Incident location, Type of damage, Incident description, Reporting channel.',
        attribution:
          'In progress · Tomer · no collective-policy pathway, no employer-number lookup',
        attributionHighlight: 'no collective-policy pathway',
      },
    ],
    actionPlan: [
      {
        id: 'add-scope-subtask',
        platform: 'jira',
        title:
          'Add the missing collective-policy pathway to the eligibility check',
        detail:
          'Create a sub-task for the employer-number eligibility lookup and link the PRD clause it implements.',
        actionLabel: 'Create sub-task',
        appliedLabel: 'Sub-task created on the eligibility check',
        gantt: {
          totalUnits: 28,
          groups: [
            { label: 'Sprint 24', sublabel: 'now', start: 0, span: 7 },
            { label: 'Sprint 25', sublabel: '~1 wk', start: 7, span: 7 },
            { label: 'Sprint 26', sublabel: '~2 wks', start: 14, span: 7 },
            {
              label: 'Sprint 27',
              sublabel: '~3 wks',
              start: 21,
              span: 7,
            },
          ],
          ticks: Array.from({ length: 28 }, (_unused, unit) => ({
            unit,
            label: '',
          })),
          todayUnit: 2,
          deadlineUnit: 13,
          deadlineLabel: 'UAT entry',
          impactTiles: [
            { label: 'UAT entry', value: '~2 wks', tone: 'deadline' },
            { label: 'Now lands', value: '~3 wks', tone: 'info' },
            { label: 'Slip', value: '~1 wk', tone: 'warn' },
            { label: 'Dependents', value: '3', tone: 'neutral' },
            { label: 'Handoffs traced', value: '4', tone: 'neutral' },
          ],
          narrative:
            'The employer-number pathway was dropped between the PRD and the eligibility-check ticket - UAT and go-live readiness are scheduled against a build that is missing it.',
          narrativeNote:
            'Applying creates a sub-task for the pathway and realigns the downstream schedule.',
          appliedNarrative:
            'The new collective-policy pathway now carries the edge case and UAT starts behind the full scope - the edge case gets tested before go-live, not discovered by it.',
          applyLabel: 'Apply & realign 3 items',
          appliedLabel: 'Realigned 3 items',
          tasks: [
            {
              ref: 'SCRUM-6',
              hideRef: true,
              description: 'Eligibility check · policy ID only',
              statusLabel: 'In build',
              statusNote: 'Ships as planned',
              team: 'IT Core',
              tone: 'dependent',
              owner: { name: 'Tomer', initials: 'TM' },
              current: { start: 0, span: 9, label: '~2 wks' },
            },
            {
              ref: 'SCRUM-6A',
              hideRef: true,
              tag: 'NEW',
              description: 'Collective-policy pathway · employer number',
              statusLabel: 'New',
              statusNote: 'Created by this action',
              team: 'IT Core',
              tone: 'slip',
              owner: { name: 'Tomer', initials: 'TM' },
              proposed: { start: 9, span: 7, label: '~1 wk' },
            },
            {
              ref: 'SCRUM-11',
              hideRef: true,
              description: '',
              statusLabel: 'Pushed',
              statusNote: 'Tomer at capacity',
              team: 'IT Core',
              tone: 'cascade',
              owner: { name: 'Tomer', initials: 'TM' },
              current: { start: 9, span: 4, label: '~4 days' },
              proposed: { start: 16, span: 4, label: '~4 days' },
            },
            {
              ref: 'SCRUM-19',
              hideRef: true,
              description: 'Claims end-to-end UAT cycle',
              statusLabel: 'Blocked',
              statusNote: 'Blocked by the new pathway',
              team: 'QA',
              tone: 'blocked',
              owner: { name: 'Unassigned', initials: '?' },
              assignCandidates: [
                { name: 'Gadi', initials: 'GD' },
                { name: 'Noa R.', initials: 'NR' },
                { name: 'Itai B.', initials: 'IB' },
              ],
              current: { start: 13, span: 7, label: '~1 wk' },
              proposed: { start: 20, span: 7, label: '~1 wk' },
              dependsOnBlocker: true,
            },
            {
              ref: 'SCRUM-23',
              hideRef: true,
              description: 'Customer-service go-live readiness',
              statusLabel: 'Dependent',
              statusNote: 'Stale',
              team: 'Customer Service',
              tone: 'dependent',
              owner: { name: 'Unassigned', initials: '?' },
              assignCandidates: [
                { name: 'Nofar', initials: 'NF' },
                { name: 'Dana K.', initials: 'DK' },
                { name: 'Ron M.', initials: 'RM' },
              ],
              current: { start: 20, span: 3, label: '~3 days' },
              proposed: { start: 25, span: 3, label: '~3 days' },
              dependsOnBlocker: true,
            },
          ],
          options: [
            {
              id: 'add-pathway-now',
              name: 'Add the pathway now',
              approach:
                'Create the missing collective-policy sub-task and re-sequence UAT and go-live behind it. Full scope, no shortcut - the dates move.',
              caveat: 'UAT entry slips ~1 wk behind the re-scoped ticket.',
              recommended: true,
              recommendationRationale:
                'The same edge case was caught in UAT on the Motor Claim flow and cost +~3 wks of rework a week before go-live. Catching it now, before UAT, costs ~1 wk - the cheap version of the same fix. Across the last handoff misses on this team, scoping the gap beat shipping-and-patching every time.',
              headline: {
                label: 'UAT entry',
                value: '+~1 wk',
                tone: 'warn',
              },
              consequences: [
                {
                  label: 'Scope',
                  value: 'Full - pathway added',
                  tone: 'good',
                },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Items moved',
                  value: '3 teams · 3 items',
                  tone: 'neutral',
                },
                {
                  label: 'Risk',
                  value: 'UAT and go-live slip ~1 wk',
                  tone: 'warn',
                },
              ],
              detail: {
                heading: 'What moves, and by how much',
                summary:
                  'Full scope held · UAT and go-live re-sequenced behind the new pathway',
                items: [
                  {
                    title: 'Collective-policy pathway',
                    status: 'New · ~1 wk',
                    detail:
                      'The employer-number eligibility lookup the PRD required, created as a sub-task and built before UAT instead of discovered in it.',
                    tone: 'added',
                  },
                  {
                    title: 'Claims end-to-end UAT',
                    status: 'Moved ~1 wk later',
                    detail:
                      'Can’t start until the pathway lands, so it re-points to the new finish - full scope, about a week later.',
                    tone: 'moved',
                  },
                  {
                    title: 'Go-live readiness',
                    status: 'Moved ~2 wks later',
                    detail:
                      'Was scheduled against the old UAT date; re-sequenced behind the full-scope UAT cycle.',
                    tone: 'moved',
                  },
                  {
                    title: 'Claim status tracking API',
                    status: 'Moved ~3 days later',
                    detail:
                      'Tomer’s own next ticket; pushed because building the pathway eats his capacity, not a finish-to-start block.',
                    tone: 'moved',
                  },
                ],
                trade:
                  'You ship the collective-policy edge case with the rest of the flow and spend nothing extra - in exchange UAT and go-live slip ~1 wk.',
              },
              moves: {},
              impactTiles: [
                { label: 'UAT entry', value: '~2 wks', tone: 'deadline' },
                { label: 'Now lands', value: '~3 wks', tone: 'info' },
                { label: 'Slip', value: '~1 wk', tone: 'warn' },
                { label: 'Dependents', value: '3', tone: 'neutral' },
                { label: 'Handoffs traced', value: '4', tone: 'neutral' },
              ],
              narrative:
                'The employer-number pathway was dropped between the PRD and the eligibility-check ticket - UAT and go-live are scheduled against a build that is missing it.',
              appliedNarrative:
                'The new collective-policy pathway now carries the edge case and UAT starts behind the full scope - the edge case gets tested before go-live, not discovered by it.',
              applyLabel: 'Apply & realign 3 items',
              appliedLabel: 'Realigned 3 items',
            },
            {
              id: 'protect-uat-entry',
              name: 'Protect UAT entry',
              approach:
                'Pair a second engineer onto the new pathway so it lands before the UAT entry and UAT keeps its date.',
              caveat:
                'Borrows Dana onto the pathway for ~1 week - her own search-filter work slips in its place.',
              headline: {
                label: 'UAT entry',
                value: 'Held · full scope',
                tone: 'good',
              },
              consequences: [
                {
                  label: 'Scope',
                  value: 'Full - pathway added',
                  tone: 'good',
                },
                {
                  label: 'Added cost',
                  value: '1 engineer for ~1 week',
                  tone: 'bad',
                },
                {
                  label: 'Side effect',
                  value: 'Dana’s search-filter work slips ~1 week',
                  tone: 'warn',
                },
                {
                  label: 'Items moved',
                  value: 'UAT and go-live hold',
                  tone: 'neutral',
                },
              ],
              detail: {
                heading: 'How the date holds, and what it costs',
                summary: 'Date held · one engineer borrowed onto the pathway',
                items: [
                  {
                    title: 'Collective-policy pathway',
                    status: 'Paired · 2 eng',
                    detail:
                      'Dana pairs with Tomer so the pathway lands about a week in - in time for UAT to keep its entry date.',
                    tone: 'moved',
                  },
                  {
                    title: 'Dana K. · borrowed',
                    status: 'Out ~1 week',
                    detail:
                      'Pulled onto the pathway for about a week. She built the individual-policy eligibility check, so she ramps in hours - her own queue is what pays for the held date.',
                    rationale:
                      'Dana wrote the original eligibility validator the eligibility check extends, so she needs no ramp on the collective-policy pathway. Her in-flight search-filter work has no downstream dependents this sprint, so borrowing her costs the least elsewhere.',
                    tone: 'added',
                  },
                  {
                    title: 'Claims search filters',
                    status: 'Slips ~1 week',
                    detail:
                      'Dana’s in-flight work; paused while she pairs on the pathway and resumes about a week later. The slip moves off UAT and onto search filters.',
                    tone: 'deferred',
                  },
                  {
                    title: 'Claims end-to-end UAT',
                    status: 'Held',
                    detail:
                      'Keeps its entry date because the pathway lands before it - the edge case is in scope without moving go-live.',
                    tone: 'kept',
                  },
                ],
                trade:
                  'You hold the UAT entry and keep the full edge case - the cost is one engineer for about a week plus a one-week slip pushed onto the search-filter work.',
              },
              moves: {
                'SCRUM-6A': {
                  proposed: { start: 9, span: 4, label: '~4 days' },
                  note: 'Paired · 2 eng',
                },
                'SCRUM-19': { proposed: null, note: 'Held' },
                'SCRUM-23': { proposed: null, note: 'Held' },
                'SCRUM-11': { proposed: null, note: 'Held' },
              },
              borrowCandidates: [
                {
                  id: 'dana',
                  recommended: true,
                  engineer: { name: 'Dana K.', initials: 'DK' },
                  source: 'Claims search filters',
                  summary:
                    'Out ~1 week pairing on the new pathway - her own search-filter work slips in her place, no other team touched.',
                  rationale:
                    'Dana wrote the original eligibility validator the eligibility check extends, so she needs no ramp on the collective-policy pathway - she lands it about a week in, a day inside the UAT entry. Her in-flight search-filter work has no downstream dependents this sprint, so borrowing her costs the least elsewhere. Intera ranked her ahead of 2 other IT Core engineers on context plus availability.',
                  outcome: {
                    label: 'UAT entry',
                    value: 'Holds the date',
                    tone: 'good',
                  },
                  sideEffect: {
                    label: 'Side effect',
                    value: 'Dana’s search-filter work slips ~1 week',
                    tone: 'warn',
                  },
                  landsValue: '~1 wk',
                  slipValue: 'Held',
                  items: [
                    {
                      title: 'Claims search filters',
                      status: 'Slips ~1 week',
                      detail:
                        'Dana’s in-flight work - paused the moment she pairs on the pathway and resumes about a week later. This is the slip that moves off UAT and onto search.',
                      tone: 'deferred',
                    },
                    {
                      title: 'Collective-policy pathway',
                      status: 'Lands in ~1 wk',
                      detail:
                        'With Dana on the validator it lands about a week in - a day inside the UAT entry, so the cycle keeps its date.',
                      tone: 'moved',
                    },
                    {
                      title: 'QA & go-live',
                      status: 'Untouched',
                      detail:
                        'UAT and go-live readiness both keep their original schedule - the held pathway is what spares them.',
                      tone: 'kept',
                    },
                  ],
                  addedTask: {
                    ref: 'PAIR-dana',
                    hideRef: true,
                    tag: 'BORROWED',
                    description: 'Dana K. paired onto the new pathway',
                    statusLabel: 'Reassigned',
                    statusNote: 'Pulled off claims search',
                    team: 'IT Core',
                    tone: 'cascade',
                    owner: { name: 'Dana K.', initials: 'DK' },
                    ownerRationale:
                      'Dana wrote the original eligibility validator the eligibility check extends, so she ramps in hours, not days - the fastest path to landing the pathway before UAT. Her in-flight search-filter work has no downstream dependents this sprint, so she is the cheapest IT Core engineer to borrow.',
                    proposed: { start: 9, span: 4, label: '~4 days' },
                  },
                },
                {
                  id: 'eitan',
                  engineer: { name: 'Eitan M.', initials: 'EM' },
                  source: 'Policy-admin console',
                  summary:
                    'Already on IT Core, so no cross-team ripple - but he ramps on the validator and UAT lands ~3 days late.',
                  rationale:
                    'Eitan is already on IT Core, so borrowing him keeps the whole cost in-team - nothing spills onto QA or Customer Service. But he has never worked the eligibility validator, so Intera estimates ~3-4 days of ramp before he is productive on the pathway, pushing UAT entry ~3 days late. Cheapest on blast radius, most expensive on time.',
                  outcome: {
                    label: 'UAT entry',
                    value: 'Slips ~3 days',
                    tone: 'warn',
                  },
                  sideEffect: {
                    label: 'Side effect',
                    value: 'No cross-team slip · cost stays in IT Core',
                    tone: 'good',
                  },
                  landsValue: '~1.5 wks',
                  slipValue: '+3',
                  moves: {
                    'SCRUM-6A': {
                      proposed: { start: 9, span: 7, label: '~1 wk' },
                      note: 'Ramping on validator',
                    },
                    'SCRUM-19': {
                      proposed: {
                        start: 16,
                        span: 7,
                        label: '~1 wk',
                      },
                      note: 'Slips ~3 days',
                    },
                    'SCRUM-23': {
                      proposed: { start: 23, span: 3, label: '~3 days' },
                    },
                    'SCRUM-11': { proposed: null, note: 'Held' },
                  },
                  items: [
                    {
                      title: 'Policy-admin console',
                      status: 'Slips ~1 sprint',
                      detail:
                        'Eitan’s in-flight work - parked while he pairs on the pathway and resumes a sprint later. The slip stays inside IT Core; no other team inherits it.',
                      tone: 'deferred',
                    },
                    {
                      title: 'Validator ramp-up',
                      status: '+3-4 days',
                      detail:
                        'He has never touched the eligibility validator, so he needs ~3-4 days to get productive before he can build the pathway - that ramp is what slips the finish by about a week.',
                      tone: 'partial',
                    },
                    {
                      title: 'QA & Customer Service',
                      status: 'Slip ~3 days',
                      detail:
                        'Unlike borrowing Dana, the pathway lands late, so UAT and go-live re-point ~3 days back - the cost stays in-team but the date does move.',
                      tone: 'moved',
                    },
                  ],
                  addedTask: {
                    ref: 'PAIR-eitan',
                    hideRef: true,
                    tag: 'BORROWED',
                    description: 'Eitan M. paired onto the new pathway',
                    statusLabel: 'Reassigned',
                    statusNote: 'Ramping on validator',
                    team: 'IT Core',
                    tone: 'cascade',
                    owner: { name: 'Eitan M.', initials: 'EM' },
                    ownerRationale:
                      'Eitan is already on IT Core, so borrowing him keeps the cost in-team - but he has never worked the eligibility validator, so a ~3-4 day ramp pushes UAT ~3 days late. Cheapest on blast radius, slowest on time.',
                    proposed: { start: 9, span: 7, label: '~1 wk' },
                  },
                },
                {
                  id: 'yael',
                  engineer: { name: 'Yael S.', initials: 'YS' },
                  source: 'Enterprise onboarding integration',
                  summary:
                    'Co-wrote the pathway spec - lands it fastest, but pulling her risks a committed customer integration.',
                  rationale:
                    'Yael co-authored the collective-policy pathway in the PRD, so she ramps in minutes and lands it about ~1 wk in, two days inside the UAT entry. The cost is the steepest: she is mid-flight on the committed enterprise integration a customer is expecting this quarter, so borrowing her puts a signed commitment at risk. Intera ranks her behind Dana only because the integration slip is harder to absorb than Dana’s search-filter slip.',
                  outcome: {
                    label: 'UAT entry',
                    value: 'Holds the date',
                    tone: 'good',
                  },
                  sideEffect: {
                    label: 'Side effect',
                    value: 'Committed enterprise integration slips ~2 weeks',
                    tone: 'bad',
                  },
                  landsValue: '~1 wk',
                  slipValue: 'Held',
                  moves: {
                    'SCRUM-6A': {
                      proposed: { start: 9, span: 3, label: '~3 days' },
                      note: 'Pathway author · 2 eng',
                    },
                    'SCRUM-19': { proposed: null, note: 'Held' },
                    'SCRUM-23': { proposed: null, note: 'Held' },
                    'SCRUM-11': { proposed: null, note: 'Held' },
                  },
                  items: [
                    {
                      title: 'Enterprise onboarding integration',
                      status: 'Slips ~2 weeks',
                      detail:
                        'Yael’s in-flight work - a customer is expecting it this quarter. Borrowing her doesn’t just delay internal work, it puts a signed external commitment at risk for ~2 weeks.',
                      tone: 'deferred',
                    },
                    {
                      title: 'Collective-policy pathway',
                      status: 'Lands in ~1 wk',
                      detail:
                        'As a co-author of the pathway spec she ramps in minutes and lands it about a week in - the earliest of the three, with two days of slack before the UAT entry.',
                      tone: 'moved',
                    },
                    {
                      title: 'Customer commitment',
                      status: 'At risk',
                      detail:
                        'The enterprise onboarding integration is the one displaced item that lands on a customer rather than another internal team - the reason Intera ranks her behind Dana.',
                      tone: 'partial',
                    },
                  ],
                  addedTask: {
                    ref: 'PAIR-yael',
                    hideRef: true,
                    tag: 'BORROWED',
                    description: 'Yael S. paired onto the new pathway',
                    statusLabel: 'Reassigned',
                    statusNote: 'Pulled off enterprise onboarding',
                    team: 'IT Core',
                    tone: 'cascade',
                    owner: { name: 'Yael S.', initials: 'YS' },
                    ownerRationale:
                      'Yael co-authored the collective-policy pathway in the PRD, so she ramps in minutes and lands it about a week in - the fastest option. The catch: she is mid-flight on the committed enterprise integration, so borrowing her puts a customer deliverable at risk.',
                    proposed: { start: 9, span: 3, label: '~3 days' },
                  },
                },
              ],
              impactTiles: [
                { label: 'UAT entry', value: '~2 wks', tone: 'deadline' },
                { label: 'Now lands', value: '~2 wks', tone: 'info' },
                { label: 'Slip', value: 'Held', tone: 'neutral' },
                { label: 'Borrowed', value: '1 eng', tone: 'warn' },
                { label: 'Handoffs traced', value: '4', tone: 'neutral' },
              ],
              narrative:
                'The pathway can land before UAT if a second engineer pairs on it - the date holds, but a week of someone else’s work moves to make room.',
              appliedNarrative:
                'Dana is paired onto the new pathway, it lands about a week in, and UAT keeps its entry date - the held date is paid for by a one-week slip on her search-filter work.',
              applyLabel: 'Pair & protect UAT entry',
              appliedLabel: 'UAT entry protected · 1 engineer borrowed',
            },
            {
              id: 'ship-policy-id-first',
              name: 'Ship policy-ID first, fast-follow',
              approach:
                'Ship the individual-policy check on time and move the collective-policy pathway to a post-go-live fast-follow behind a flag.',
              caveat:
                'Collective workplace policies can’t be processed until the fast-follow - the dropped edge case ships unhandled.',
              headline: {
                label: 'UAT entry',
                value: 'Held · scope cut',
                tone: 'warn',
              },
              consequences: [
                {
                  label: 'Scope',
                  value: 'Collective pathway deferred',
                  tone: 'bad',
                },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Risk',
                  value: 'Edge case unhandled at go-live',
                  tone: 'bad',
                },
                {
                  label: 'Items moved',
                  value: 'UAT and go-live hold',
                  tone: 'neutral',
                },
              ],
              detail: {
                heading: 'What ships now, and what doesn’t',
                summary:
                  'Date held · collective-policy pathway pulled to a fast-follow',
                items: [
                  {
                    title: 'Eligibility check · policy ID',
                    status: 'Ships now',
                    detail:
                      'The individual-policy flow ships on its original date - UAT runs against it as planned.',
                    tone: 'kept',
                  },
                  {
                    title: 'Collective-policy pathway',
                    status: 'Deferred to fast-follow',
                    detail:
                      'The employer-number pathway moves behind a flag to a post-go-live release - the same edge case that broke Motor Claim ships unhandled until it lands.',
                    tone: 'deferred',
                  },
                  {
                    title: 'Claims end-to-end UAT',
                    status: 'Held · policy-ID scope',
                    detail:
                      'Keeps its entry date, but only exercises the individual-policy path - collective policies are out of the tested scope.',
                    tone: 'partial',
                  },
                ],
                trade:
                  'You hold every date and spend nothing - in exchange collective workplace policies can’t be processed at go-live, the exact gap that cost Motor Claim three weeks in UAT.',
              },
              moves: {
                'SCRUM-6A': {
                  proposed: { start: 21, span: 7, label: '~1 wk' },
                  note: 'Fast-follow',
                },
                'SCRUM-19': {
                  proposed: null,
                  note: 'Runs on policy-ID scope',
                },
                'SCRUM-23': { proposed: null, note: 'Held' },
                'SCRUM-11': { proposed: null, note: 'Held' },
              },
              impactTiles: [
                { label: 'UAT entry', value: '~2 wks', tone: 'deadline' },
                { label: 'Now lands', value: '~2 wks', tone: 'info' },
                { label: 'Slip', value: 'Held', tone: 'neutral' },
                { label: 'Pathway', value: 'Deferred', tone: 'warn' },
                { label: 'Handoffs traced', value: '4', tone: 'neutral' },
              ],
              narrative:
                'The date can hold if the collective-policy pathway is pulled to a fast-follow - but go-live then ships without the edge case the PRD required.',
              appliedNarrative:
                'The eligibility check ships on time and the collective-policy pathway moves behind a flag to a fast-follow - the dates hold, but the edge case is unhandled until it lands.',
              applyLabel: 'Ship policy-ID & defer pathway',
              appliedLabel: 'Shipped on policy-ID scope · pathway deferred',
            },
          ],
        },
        timeline: {
          axis: ['now', '~1 wk', '~2 wks'],
          deadlineIndex: 0,
          delayLabel: '+1 week',
          rows: [
            {
              label: 'Eligibility check',
              role: 'Re-scoped',
              kind: 'slip',
              fromIndex: 0,
              toIndex: 1,
              isPrimary: true,
              note: 'employer-number pathway added to scope',
            },
            {
              label: 'Claims UAT',
              role: 'Dependent',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 1,
            },
          ],
          shiftNote:
            'The collective-policy pathway adds a week to the eligibility check - but UAT is still scheduled as if scope was unchanged. Pull it behind the re-scoped ticket.',
          afterNote:
            'UAT now starts behind the full scope - the edge case gets tested before go-live instead of breaking it.',
        },
      },
    ],
  },

  // ─── Playbook · recurring mistake · single-source (the edge case) ───
  {
    id: 'add-driver-prd-missing-collective',
    category: 'playbook',
    playbookType: 'recurring-mistake',
    severity: 'high',
    playbook:
      'Collective workplace policies are an edge case - they need a separate pathway, not the individual-policy flow.',
    team: 'Motor Policies',
    title: 'New PRD is missing the collective-policy edge case',
    summary:
      'The Add Driver to Motor Policy PRD assumes an individual policy - the same edge case that broke the Motor Claim flow in UAT.',
    detail:
      'The Add Driver to Motor Policy PRD assumes an individual policy. No collective-workplace pathway, no employer-arrangement check on who can be added. This is the exact edge case that surfaced a week before the Motor Claim go-live - Intera flagged it on the new PRD before build starts.',
    detectedAgo: '3h ago',
    evidence: [
      {
        platform: 'confluence',
        ref: 'Add Driver to Motor Policy PRD',
        url: 'https://intera-ai-demo.atlassian.net/wiki/spaces/MT/pages/6881281/Add+Driver+to+Motor+Policy+PRD?focusedCommentId=7569409',
        quote:
          'The request is validated against the individual motor policy and the premium is recalculated. The flow must also account for cases that diverge from the main flow.',
        highlight:
          'The flow must also account for cases that diverge from the main flow',
        attribution: 'Draft v2 · Product',
      },
    ],
    actionPlan: [
      {
        id: 'comment-on-add-driver-prd',
        platform: 'confluence',
        title: 'Flag the missing pathway on the Add Driver PRD',
        detail:
          'Intera left an inline comment on the eligibility section asking Product to define the collective-workplace pathway.',
        actionLabel: 'Add comment',
        appliedLabel: 'Inline comment added to the PRD',
        preApplied: true,
      },
      {
        id: 'open-scoping-ticket',
        platform: 'jira',
        title: 'Open a scoping ticket for the collective pathway',
        detail:
          'Create a ticket to scope employer-arrangement checks for Add Driver before the build is planned.',
        actionLabel: 'Create ticket',
        appliedLabel: 'Scoping ticket created',
        gantt: {
          totalUnits: 28,
          groups: [
            { label: 'Sprint 24', sublabel: 'now', start: 0, span: 7 },
            { label: 'Sprint 25', sublabel: '~1 wk', start: 7, span: 7 },
            { label: 'Sprint 26', sublabel: '~2 wks', start: 14, span: 7 },
            {
              label: 'Sprint 27',
              sublabel: '~3 wks',
              start: 21,
              span: 7,
            },
          ],
          ticks: Array.from({ length: 28 }, (_unused, unit) => ({
            unit,
            label: '',
          })),
          todayUnit: 2,
          deadlineUnit: 14,
          deadlineLabel: 'Build start',
          impactTiles: [
            { label: 'Build start', value: '~2 wks', tone: 'deadline' },
            { label: 'Now starts', value: '~3 wks', tone: 'info' },
            { label: 'Slip', value: '~1 wk', tone: 'warn' },
            { label: 'Caught', value: 'Pre-build', tone: 'neutral' },
            {
              label: 'Motor Claim cost',
              value: '+21',
              unit: 'days',
              tone: 'neutral',
            },
          ],
          narrative:
            'The Add Driver PRD repeats the Motor Claim miss - build is scheduled to start in ~2 wks against a spec with no collective-policy pathway.',
          narrativeNote:
            'Applying opens a scoping work item and shifts build by one week.',
          appliedNarrative:
            'The pathway is scoped before a line of code is written - a +~1 wk shift now instead of the +~3 wks Motor Claim took in UAT.',
          applyLabel: 'Apply & rescope build',
          appliedLabel: 'Build rescoped',
          tasks: [
            {
              ref: 'ADD-PRD',
              hideRef: true,
              tag: 'DOC',
              description: 'Add Driver to Motor Policy PRD',
              statusLabel: 'Drafted',
              statusNote: 'Missing edge case',
              team: 'Product',
              tone: 'dependent',
              owner: { name: 'Ran', initials: 'RN' },
              current: { start: 0, span: 8, label: '~1.5 wks' },
            },
            {
              ref: 'ADD-260',
              hideRef: true,
              tag: 'NEW',
              description: 'Collective-pathway scoping · employer arrangements',
              statusLabel: 'New',
              statusNote: 'Created by this action',
              team: 'Product',
              tone: 'slip',
              owner: { name: 'Unassigned', initials: '?' },
              assignCandidates: [
                { name: 'Ran', initials: 'RN' },
                { name: 'Maya L.', initials: 'ML' },
                { name: 'Tom S.', initials: 'TS' },
              ],
              proposed: { start: 8, span: 6, label: '~1 wk' },
            },
            {
              ref: 'ADD-201',
              hideRef: true,
              description: 'Add Driver flow · app screens & API',
              statusLabel: 'Blocked',
              statusNote: 'Blocked by the new pathway',
              team: 'IT Digital',
              tone: 'blocked',
              owner: { name: 'Yael', initials: 'YL' },
              current: { start: 14, span: 7, label: '~1 wk' },
              proposed: { start: 21, span: 7, label: '~1 wk' },
              dependsOnBlocker: true,
            },
            {
              ref: 'ADD-230',
              hideRef: true,
              description:
                'Regression pass · Add Driver collective-policy cases',
              statusLabel: 'Dependent',
              statusNote: 'Stale',
              team: 'QA',
              tone: 'dependent',
              owner: { name: 'Unassigned', initials: '?' },
              assignCandidates: [
                { name: 'Gadi', initials: 'GD' },
                { name: 'Noa R.', initials: 'NR' },
                { name: 'Itai B.', initials: 'IB' },
              ],
              current: { start: 18, span: 3, label: '~3 days' },
              proposed: { start: 25, span: 3, label: '~3 days' },
              dependsOnBlocker: true,
            },
          ],
          options: [
            {
              id: 'scope-before-build',
              name: 'Scope before build',
              approach:
                'Open the scoping ticket for the collective pathway and start build behind it. The spec is complete before a line of code - build start moves a week.',
              caveat: 'Build start slips ~1 wk while the pathway is scoped.',
              recommended: true,
              recommendationRationale:
                'This is the exact edge case that surfaced a week before the Motor Claim go-live and cost +~3 wks of UAT rework. Scoping it before build costs +~1 wk - the same fix, an order of magnitude cheaper. Intera flagged it on the PRD before build was planned precisely so it could be caught here.',
              headline: {
                label: 'Build start',
                value: '+~1 wk',
                tone: 'warn',
              },
              consequences: [
                {
                  label: 'Scope',
                  value: 'Full - pathway scoped',
                  tone: 'good',
                },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Items moved',
                  value: '3 teams · 3 items',
                  tone: 'neutral',
                },
                {
                  label: 'Risk',
                  value: 'Build and test slip ~1 wk',
                  tone: 'warn',
                },
              ],
              detail: {
                heading: 'What moves, and by how much',
                summary:
                  'Edge case scoped pre-build · build and test re-sequenced behind it',
                items: [
                  {
                    title: 'Collective-pathway scoping',
                    status: 'New · ~1 wk',
                    detail:
                      'Defines the employer-arrangement checks the PRD left out, before build starts instead of after UAT breaks.',
                    tone: 'added',
                  },
                  {
                    title: 'Add Driver flow · app & API',
                    status: 'Moved ~1 wk later',
                    detail:
                      'Can’t start until the pathway is scoped, so build re-points to the new finish - full scope, a week later.',
                    tone: 'moved',
                  },
                  {
                    title: 'Add Driver test plan',
                    status: 'Moved ~2 wks later',
                    detail:
                      'Was scheduled against the old build date; re-sequenced behind the full-scope build.',
                    tone: 'moved',
                  },
                ],
                trade:
                  'You scope the collective-workplace edge case before a line of code and spend nothing extra - in exchange build start slips ~1 wk.',
              },
              moves: {},
              impactTiles: [
                { label: 'Build start', value: '~2 wks', tone: 'deadline' },
                { label: 'Now starts', value: '~3 wks', tone: 'info' },
                { label: 'Slip', value: '~1 wk', tone: 'warn' },
                { label: 'Caught', value: 'Pre-build', tone: 'neutral' },
                {
                  label: 'Motor Claim cost',
                  value: '+21',
                  unit: 'days',
                  tone: 'neutral',
                },
              ],
              narrative:
                'The Add Driver PRD repeats the Motor Claim miss - build is scheduled to start in ~2 wks against a spec with no collective-policy pathway.',
              appliedNarrative:
                'The pathway is scoped before build starts - a +~1 wk shift now instead of the +~3 wks Motor Claim took in UAT.',
              applyLabel: 'Apply & rescope build',
              appliedLabel: 'Build rescoped',
            },
            {
              id: 'scope-in-parallel',
              name: 'Scope in parallel',
              approach:
                'Start build on the validated individual-policy flow now and scope the collective pathway alongside it, folding it in before the flow ships.',
              caveat:
                'If scoping changes the eligibility data model, the in-flight build needs rework.',
              headline: {
                label: 'Build start',
                value: 'Held · full scope',
                tone: 'good',
              },
              consequences: [
                { label: 'Scope', value: 'Full - folded in', tone: 'good' },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Risk',
                  value: 'Rework if the data model shifts',
                  tone: 'warn',
                },
                {
                  label: 'Items moved',
                  value: 'Build holds · scoping parallel',
                  tone: 'neutral',
                },
              ],
              detail: {
                heading: 'How build keeps its date, and what it risks',
                summary:
                  'Build holds its date · pathway scoped alongside, not before',
                items: [
                  {
                    title: 'Add Driver flow · app & API',
                    status: 'Held',
                    detail:
                      'Starts on time against the validated individual-policy flow, which the PRD already specifies fully.',
                    tone: 'kept',
                  },
                  {
                    title: 'Collective-pathway scoping',
                    status: 'Parallel · ~1 wk',
                    detail:
                      'Runs alongside the build instead of before it, and folds into the flow before it ships.',
                    tone: 'moved',
                  },
                  {
                    title: 'Add Driver test plan',
                    status: 'Held',
                    detail:
                      'Keeps its date - it now has to cover both the main flow and the folded-in pathway in the same window.',
                    tone: 'partial',
                  },
                ],
                trade:
                  'You hold the build start and still ship the edge case - the risk is that if scoping changes the eligibility data model, the part of the build already in flight has to be reworked.',
              },
              moves: {
                'ADD-201': { proposed: null, note: 'Held' },
                'ADD-260': {
                  proposed: { start: 14, span: 6, label: '~1 wk' },
                  note: 'Parallel scoping',
                },
                'ADD-230': { proposed: null, note: 'Held' },
              },
              impactTiles: [
                { label: 'Build start', value: '~2 wks', tone: 'deadline' },
                { label: 'Now starts', value: '~2 wks', tone: 'info' },
                { label: 'Slip', value: 'Held', tone: 'neutral' },
                { label: 'Risk', value: 'Rework', tone: 'warn' },
                {
                  label: 'Motor Claim cost',
                  value: '+21',
                  unit: 'days',
                  tone: 'neutral',
                },
              ],
              narrative:
                'Build can keep its date if the pathway is scoped in parallel - but the in-flight build is exposed to rework if scoping moves the data model.',
              appliedNarrative:
                'Build starts on schedule on the individual-policy flow with the collective pathway scoped alongside - the date holds, with rework risk if the data model shifts.',
              applyLabel: 'Start build & scope in parallel',
              appliedLabel: 'Build started · pathway scoped in parallel',
            },
            {
              id: 'defer-collective-pathway',
              name: 'Build main flow, defer pathway',
              approach:
                'Ship Add Driver for individual policies on schedule and defer the collective-workplace pathway to a later release.',
              caveat:
                'Repeats the exact edge-case miss that broke Motor Claim - collective policies stay unsupported.',
              headline: {
                label: 'Build start',
                value: 'Held · scope cut',
                tone: 'warn',
              },
              consequences: [
                {
                  label: 'Scope',
                  value: 'Collective pathway deferred',
                  tone: 'bad',
                },
                { label: 'Added cost', value: 'None', tone: 'good' },
                {
                  label: 'Risk',
                  value: 'Same gap that broke Motor Claim',
                  tone: 'bad',
                },
                {
                  label: 'Items moved',
                  value: 'Build and test hold',
                  tone: 'neutral',
                },
              ],
              detail: {
                heading: 'What ships now, and what doesn’t',
                summary:
                  'Date held · collective-workplace pathway pushed to a later release',
                items: [
                  {
                    title: 'Add Driver flow · app & API',
                    status: 'Ships on schedule',
                    detail:
                      'Builds the individual-policy flow on its original date - the main path ships unblocked.',
                    tone: 'kept',
                  },
                  {
                    title: 'Collective-pathway scoping',
                    status: 'Deferred to later release',
                    detail:
                      'The employer-arrangement pathway is pushed out - the same edge case that broke Motor Claim ships unsupported again.',
                    tone: 'deferred',
                  },
                  {
                    title: 'Add Driver test plan',
                    status: 'Held · individual scope',
                    detail:
                      'Keeps its date, but only covers the individual-policy flow - the collective case is out of tested scope.',
                    tone: 'partial',
                  },
                ],
                trade:
                  'You hold every date and spend nothing - in exchange Add Driver ships without the collective-workplace pathway, the exact gap that cost Motor Claim three weeks in UAT.',
              },
              moves: {
                'ADD-260': {
                  proposed: { start: 21, span: 7, label: '~1 wk' },
                  note: 'Deferred to later release',
                },
                'ADD-201': {
                  proposed: null,
                  note: 'Ships on individual scope',
                },
                'ADD-230': { proposed: null, note: 'Held' },
              },
              impactTiles: [
                { label: 'Build start', value: '~2 wks', tone: 'deadline' },
                { label: 'Now starts', value: '~2 wks', tone: 'info' },
                { label: 'Slip', value: 'Held', tone: 'neutral' },
                { label: 'Pathway', value: 'Deferred', tone: 'warn' },
                {
                  label: 'Motor Claim cost',
                  value: '+21',
                  unit: 'days',
                  tone: 'neutral',
                },
              ],
              narrative:
                'The date can hold if the collective pathway is deferred - but Add Driver then ships with the same gap that broke the Motor Claim flow in UAT.',
              appliedNarrative:
                'Add Driver ships on schedule for individual policies and the collective-workplace pathway moves to a later release - the dates hold, the edge case does not.',
              applyLabel: 'Ship main flow & defer pathway',
              appliedLabel: 'Main flow shipped · pathway deferred',
            },
          ],
        },
        timeline: {
          axis: ['now', '~1 wk', '~2 wks'],
          deadlineIndex: 0,
          delayLabel: '+1 week',
          rows: [
            {
              label: 'Add Driver PRD',
              role: 'Re-scoped',
              kind: 'slip',
              fromIndex: 0,
              toIndex: 1,
              isPrimary: true,
              note: 'collective pathway defined before build',
            },
            {
              label: 'Build start',
              role: 'Dependent',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 1,
            },
          ],
          shiftNote:
            'Scoping the collective pathway moves build start by a week - the cheap version of this slip. Caught in UAT, it cost Motor Claim three weeks.',
          afterNote:
            'Build starts with the edge case in scope - no UAT surprise, no go-live slip.',
        },
      },
    ],
  },

  // ─── Playbook · recurring mistake · single-source (GitHub) ───
  {
    id: 'migration-no-rollback',
    category: 'playbook',
    playbookType: 'missing-rollback',
    severity: 'medium',
    playbook:
      'Every database migration PR must include a Rollback section before it can be reviewed - no exceptions on prod tables.',
    team: 'Core Infrastructure',
    title:
      'Migration PR opened without a rollback plan - 3rd time this quarter',
    summary:
      'PR #4821 changes a schema with no rollback section, the same playbook step missed three times this quarter.',
    detail:
      'The migration PR has no rollback documentation in its description, which the team playbook makes mandatory for schema changes. Intera has now seen this exact step skipped three times this quarter on this repo.',
    detectedAgo: '4h ago',
    evidence: [
      {
        platform: 'github',
        ref: 'PR #4821',
        url: `${GITHUB}/core-db/pull/4821`,
        quote:
          'Adds non-null column `tenant_id` to `invoices`. Backfill included. (No "Rollback" section present.)',
        highlight: 'No "Rollback" section present',
        attribution: 'opened by jdoe · 4h ago',
      },
    ],
    actionPlan: [
      {
        id: 'request-rollback',
        platform: 'github',
        title: 'Request changes with the rollback checklist',
        detail:
          'Post the standard rollback-plan checklist as a review comment on PR #4821.',
        actionLabel: 'Request changes',
        appliedLabel: 'Review requested',
      },
      {
        id: 'pin-playbook',
        platform: 'slack',
        title: 'Re-share the migration playbook in #core-infra',
        detail:
          'This is the 3rd miss - surface the playbook so the pattern stops.',
        actionLabel: 'Share playbook',
        appliedLabel: 'Playbook shared',
        preApplied: true,
      },
    ],
  },

  // ─── Playbook · team not looped in · cross-platform (Slack + Jira) ───
  {
    id: 'gtm-shift-sprint-unchanged',
    category: 'playbook',
    playbookType: 'team-not-looped-in',
    severity: 'high',
    playbook:
      'When GTM moves a launch date, Growth Engineering must be pulled into the channel to re-scope the sprint the same day.',
    team: 'Growth Engineering',
    title: 'GTM delayed the promo 2 weeks - the eng sprint scope never changed',
    summary:
      'Marketing agreed in Slack to push the launch, but the backend promo features are still top priority this sprint.',
    detail:
      'The GTM team agreed in #gtm-planning to delay the promo by two weeks. The backend promo-engine tickets are still marked highest priority in the current sprint, so engineering is burning down work against a date that moved.',
    detectedAgo: '6h ago',
    evidence: [
      {
        platform: 'slack',
        ref: '#gtm-planning',
        url: `${SLACK}/C0GTMPLAN`,
        quote:
          "Let's move the promo to the 28th - creative isn't ready and we'd rather not rush it.",
        highlight: 'move the promo to the 28th',
        attribution: 'Lena (GTM) · 6h ago',
      },
      {
        platform: 'jira',
        ref: 'GROW-203',
        url: `${JIRA}/GROW-203`,
        quote:
          'Promo engine - discount rules. Priority: Highest. Sprint: current. No date change.',
        highlight: 'Priority: Highest',
        attribution: 'unchanged',
      },
    ],
    actionPlan: [
      {
        id: 'reprioritize-promo',
        platform: 'jira',
        title: 'Lower priority and re-target the promo tickets',
        detail: 'Move GROW-203 and siblings to next sprint, target the 28th.',
        actionLabel: 'Re-prioritize',
        appliedLabel: 'Re-prioritized to next sprint',
        gantt: {
          totalUnits: 14,
          groups: [
            { label: 'This sprint', start: 0, span: 7 },
            {
              label: 'Next sprint',
              sublabel: 'promo · the 28th',
              start: 7,
              span: 7,
            },
          ],
          ticks: [
            { unit: 0, label: 'This sprint' },
            { unit: 7, label: 'Next sprint' },
          ],
          todayUnit: 3,
          deadlineUnit: 0,
          deadlineLabel: 'Old launch',
          impactTiles: [
            {
              label: 'Launch moved to',
              value: 'Next sprint',
              tone: 'deadline',
            },
            { label: 'Promo work', value: 'Next sprint', tone: 'info' },
            {
              label: 'Burned on stale plan',
              value: '+9',
              unit: 'days',
              tone: 'warn',
            },
            { label: 'Tickets', value: '2', tone: 'neutral' },
          ],
          narrative:
            'GTM moved the launch to next sprint, but the promo tickets are still top priority this sprint.',
          narrativeNote:
            'Move them to match so nobody burns down against a date that moved.',
          appliedNarrative:
            'Promo work now lands next sprint aligned with the launch, and Ravi’s roadmap ticket is freed back into this sprint - no more burndown against a moved date.',
          applyLabel: 'Re-prioritize 3 tickets',
          appliedLabel: 'Re-prioritized',
          tasks: [
            {
              ref: 'Promo launch',
              description: 'GTM moved to the 28th',
              statusLabel: 'Moved',
              statusNote: 'GTM decision',
              team: 'GTM / Marketing',
              tone: 'slip',
              owner: { name: 'Lena Ortiz', initials: 'LO' },
              current: { start: 0, span: 6, label: 'This sprint' },
              proposed: { start: 7, span: 6, label: 'Next sprint' },
            },
            {
              ref: 'GROW-203',
              description: 'Promo banner + tracking',
              statusLabel: 'Dependent',
              statusNote: 'Top priority',
              team: 'Growth Engineering',
              tone: 'dependent',
              owner: { name: 'Ravi K.', initials: 'RK' },
              current: { start: 1, span: 5, label: 'This sprint' },
              proposed: { start: 8, span: 5, label: 'Next sprint' },
              dependsOnBlocker: true,
            },
            {
              ref: 'GROW-204',
              description: 'Promo email sequence',
              statusLabel: 'Dependent',
              statusNote: 'Top priority',
              team: 'Growth Engineering',
              tone: 'dependent',
              owner: { name: 'Tara S.', initials: 'TS' },
              current: { start: 2, span: 4, label: 'This sprint' },
              proposed: { start: 9, span: 4, label: 'Next sprint' },
              dependsOnBlocker: true,
            },
            {
              ref: 'GROW-210',
              description: 'Referral revamp · roadmap',
              statusLabel: 'Pushed',
              statusNote: 'Crowded out by promo',
              team: 'Growth Engineering',
              tone: 'cascade',
              owner: { name: 'Ravi K.', initials: 'RK' },
              current: { start: 7, span: 5, label: 'Next sprint' },
              proposed: { start: 1, span: 5, label: 'This sprint' },
            },
          ],
        },
        timeline: {
          axis: ['This sprint', 'Next sprint'],
          deadlineIndex: 0,
          delayLabel: '1 sprint',
          rows: [
            {
              label: 'Promo launch',
              role: 'Moved',
              kind: 'slip',
              fromIndex: 0,
              toIndex: 1,
              isPrimary: true,
              note: 'GTM pushed to the 28th',
            },
            {
              label: 'GROW-203',
              role: 'Promo',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 1,
            },
            {
              label: 'GROW-204',
              role: 'Promo',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 1,
            },
          ],
          shiftNote:
            'GTM moved the launch to next sprint - but the promo tickets are still top priority this sprint. Move them to match.',
          afterNote:
            'Promo work now lands next sprint, aligned with the launch - no more burndown against a date that moved.',
        },
      },
      {
        id: 'confirm-with-gtm',
        platform: 'slack',
        title: 'Confirm the new date with GTM and Eng',
        detail:
          'Cross-post the agreed date so both sides are working to the same plan.',
        actionLabel: 'Send confirm',
        appliedLabel: 'Confirmation sent',
      },
    ],
  },

  // ─── Playbook · stale dependency · cross-platform (Jira + PagerDuty) ───
  {
    id: 'stale-dependency-incident',
    category: 'playbook',
    playbookType: 'stale-dependency',
    severity: 'high',
    playbook:
      'When a ticket on the payments idempotency layer is reopened, every dependent ticket must be re-blocked until it is re-verified.',
    team: 'Payments Platform',
    title:
      'A "done" dependency reopened after a P1 - dependents not reverified',
    summary:
      'PAY-9281 reopened post-incident, but the three tickets that depend on it are still marked unblocked.',
    detail:
      'The idempotency-key ticket was reopened after INC-2041, a duplicate-charge P1. Three downstream tickets list it as a resolved dependency and remain "ready", so they are building on a foundation that is no longer stable.',
    detectedAgo: '3h ago',
    evidence: [
      {
        platform: 'pagerduty',
        ref: 'INC-2041',
        url: `${PAGERDUTY}/INC-2041`,
        quote:
          'P1 - duplicate charges on retried webhooks. Root cause traced to idempotency key reuse.',
        highlight: 'duplicate charges on retried webhooks',
        attribution: 'resolved · 9h',
      },
      {
        platform: 'jira',
        ref: 'PAY-9281',
        url: `${JIRA}/PAY-9281`,
        quote:
          'Reopened. The dedupe layer needs a rewrite - current keys collide on the refunds path.',
        highlight: 'Reopened',
        attribution: 'reopened today',
      },
    ],
    actionPlan: [
      {
        id: 'flag-dependents',
        platform: 'jira',
        title: 'Re-block the 3 dependent tickets',
        detail:
          'Mark the dependents blocked-by PAY-9281 until the rewrite lands.',
        actionLabel: 'Re-block',
        appliedLabel: '3 tickets re-blocked',
      },
      {
        id: 'page-owners',
        platform: 'slack',
        title: 'Alert the dependent ticket owners',
        detail:
          'Notify owners that the dependency reopened and work should pause.',
        actionLabel: 'Notify owners',
        appliedLabel: 'Owners notified',
      },
    ],
  },

  // ─── Playbook · resource overload · single-source (Jira) ───
  {
    id: 'engineer-overloaded',
    category: 'playbook',
    playbookType: 'resource-overload',
    severity: 'medium',
    playbook:
      'No single engineer may own more than two critical-path tickets in the reranking epic in one sprint.',
    team: 'Search',
    title: 'One senior engineer owns 4 critical-path tickets this sprint',
    summary:
      'All four reranking tickets on the critical path are assigned to the same person - the sprint cannot finish on time.',
    detail:
      'Four critical-path tickets in the reranking epic are assigned to a single engineer. Their combined estimate exceeds one person’s sprint capacity, so at least one will slip regardless of effort.',
    detectedAgo: '1d ago',
    evidence: [
      {
        platform: 'jira',
        ref: 'SRCH-771…774',
        url: `${JIRA}/SRCH-771`,
        quote:
          'SRCH-771, 772, 773, 774 - all critical path, all assigned to a.ng, total 18 points in a 10-point capacity.',
        highlight: '18 points in a 10-point capacity',
        attribution: 'current sprint',
      },
    ],
    actionPlan: [
      {
        id: 'rebalance',
        platform: 'jira',
        title: 'Re-assign 2 tickets to engineers with capacity',
        detail:
          'Move SRCH-773 and SRCH-774 to teammates under capacity this sprint.',
        actionLabel: 'Rebalance',
        appliedLabel: '2 tickets re-assigned',
      },
    ],
  },

  // ─── Commitment · cross-platform (CRM + Jira) - customer-facing promise ───
  {
    id: 'crm-promise-vs-backlog',
    category: 'commitment',
    severity: 'high',
    team: 'Integrations',
    title:
      'Sales promised an SSO feature to a customer that is still in the backlog',
    summary:
      'A signed CRM opportunity commits SAML SSO by Aug 1, but the Jira ticket is unstarted in the backlog.',
    detail:
      'A closed-won opportunity in Dynamics records a contractual commitment to ship SAML SSO by August 1. The implementing Jira ticket is still in the backlog with no assignee - a commitment with no execution behind it.',
    detectedAgo: '2d ago',
    evidence: [
      {
        platform: 'dynamics-crm',
        ref: 'OPP-5567 - Northwind',
        url: `${CRM}&etn=opportunity&id=OPP-5567`,
        quote:
          'Closed-won. Contract note: customer requires SAML SSO live by Aug 1 as a condition of renewal.',
        highlight: 'SAML SSO live by Aug 1',
        attribution: 'Account: Northwind',
      },
      {
        platform: 'jira',
        ref: 'INT-980',
        url: `${JIRA}/INT-980`,
        quote:
          'SAML SSO support. Status: Backlog. Assignee: unassigned. Estimate: none.',
        highlight: 'Status: Backlog',
        attribution: 'never started',
      },
    ],
    actionPlan: [
      {
        id: 'schedule-sso',
        platform: 'jira',
        title: 'Pull SSO into the next sprint and assign an owner',
        detail:
          'Move INT-980 out of backlog, set the Aug 1 due date, assign a lead.',
        actionLabel: 'Schedule',
        appliedLabel: 'Scheduled for next sprint',
        gantt: {
          totalUnits: 21,
          groups: [
            { label: 'Backlog', start: 0, span: 7 },
            { label: 'Next sprint', start: 7, span: 7 },
            { label: 'Aug 1 due', sublabel: 'contractual', start: 14, span: 7 },
          ],
          ticks: [
            { unit: 0, label: 'Backlog' },
            { unit: 7, label: 'Next sprint' },
            { unit: 18, label: 'Aug 1' },
          ],
          todayUnit: 1,
          deadlineUnit: 18,
          deadlineLabel: 'Aug 1 commitment',
          impactTiles: [
            { label: 'Commitment', value: 'Aug 1', tone: 'deadline' },
            { label: 'Now scheduled', value: 'Next sprint', tone: 'info' },
            {
              label: 'Buffer to deadline',
              value: '~1',
              unit: 'sprint',
              tone: 'neutral',
            },
            { label: 'Status today', value: 'No owner', tone: 'warn' },
          ],
          narrative:
            'INT-980 is sitting in the backlog with no owner and the Aug 1 deadline approaching.',
          narrativeNote:
            'Pull it into next sprint and assign a lead so it lands in time.',
          appliedNarrative:
            'INT-980 scheduled with an owner next sprint - comfortably ahead of the Aug 1 commitment.',
          applyLabel: 'Schedule & assign owner',
          appliedLabel: 'Scheduled for next sprint',
          tasks: [
            {
              ref: 'INT-980',
              description: 'SSO for the enterprise tier',
              statusLabel: 'Unstarted',
              statusNote: 'No owner',
              team: 'Integrations',
              tone: 'dependent',
              owner: { name: 'Unassigned', initials: '—' },
              current: { start: 0, span: 4, label: 'In backlog' },
              proposed: { start: 7, span: 4, label: 'Next sprint' },
            },
          ],
        },
        timeline: {
          axis: ['Backlog', 'Next sprint', 'Aug 1 due'],
          deadlineIndex: 2,
          delayLabel: '1 sprint of runway',
          rows: [
            {
              label: 'INT-980 · SSO',
              role: 'Unstarted',
              kind: 'dependent',
              fromIndex: 0,
              toIndex: 1,
              isPrimary: true,
              note: 'no owner · contractual Aug 1',
            },
          ],
          shiftNote:
            'INT-980 is sitting in the backlog with the Aug 1 deadline approaching. Pull it into next sprint so it lands in time.',
          afterNote:
            'INT-980 scheduled with an owner next sprint - comfortably ahead of the Aug 1 commitment.',
        },
      },
      {
        id: 'notify-account',
        platform: 'slack',
        title: 'Loop in the account owner on feasibility',
        detail:
          'Flag to the AE that Aug 1 is tight so expectations stay aligned.',
        actionLabel: 'Notify AE',
        appliedLabel: 'Account owner looped in',
      },
    ],
  },

  // ─── Playbook · recurring mistake · cross-platform (Confluence + Jira) ───
  {
    id: 'api-contract-drift',
    category: 'playbook',
    playbookType: 'recurring-mistake',
    severity: 'medium',
    playbook:
      'Any change to a public API response shape must update the contract doc and post to #api-consumers in the same PR.',
    team: 'Platform API',
    title: 'Shipped endpoint drifted from the documented API contract again',
    summary:
      'The deployed response shape no longer matches the Confluence API spec - the second contract drift this month.',
    detail:
      'The orders endpoint now returns a nested `customer` object, but the published API contract still documents a flat `customerId`. Consumers integrating against the docs will break. This is the second documented-contract drift on this service this month.',
    detectedAgo: '8h ago',
    evidence: [
      {
        platform: 'confluence',
        ref: 'Orders API Contract',
        url: `${CONFLUENCE}/552210/Orders-API-Contract`,
        quote:
          'Response: { orderId, customerId, total }. customerId is a flat string.',
        highlight: 'customerId is a flat string',
        attribution: 'spec · not updated',
      },
      {
        platform: 'github',
        ref: 'PR #5102',
        url: `${GITHUB}/orders-svc/pull/5102`,
        quote:
          'Refactor: replace customerId with a nested customer { id, name } object in the response.',
        highlight: 'nested customer { id, name }',
        attribution: 'merged · 8h ago',
      },
    ],
    actionPlan: [
      {
        id: 'update-contract',
        platform: 'confluence',
        title: 'Update the API contract to match what shipped',
        detail:
          'Sync the documented response shape to the nested customer object.',
        actionLabel: 'Update doc',
        appliedLabel: 'Contract updated',
      },
      {
        id: 'notify-consumers',
        platform: 'slack',
        title: 'Warn known consumers of the breaking change',
        detail:
          'Post a breaking-change notice to #api-consumers with the diff.',
        actionLabel: 'Send notice',
        appliedLabel: 'Consumers notified',
      },
    ],
  },

  // ─── Playbook · wrong estimation · single-source (Jira, Intera-enriched with history) ───
  {
    id: 'spike-undersized',
    category: 'playbook',
    playbookType: 'wrong-estimation',
    severity: 'medium',
    playbook:
      'An estimate that contradicts the team’s own history on tickets of the same shape must be re-pointed before the sprint commits to it.',
    team: 'Mobile',
    title:
      'MOB-410 is pointed at 3 - the last 3 tickets like it shipped at ~12',
    summary:
      'The offline background-sync task is estimated at 3 points. Intera matched it to the team’s last three sync-and-retry tickets - each shipped between 11 and 13 - and flagged it at planning, before the sprint locked.',
    detail:
      'MOB-410 (offline background-sync) was pointed at 3 in planning. Intera compared it to the team’s last three sync-and-retry tickets - MOB-228, MOB-301 and MOB-377 - which were all estimated low and shipped at 11, 13 and 12 points once the retry and conflict cases were understood. The estimate is wrong on day one; the history says so, and there is still time to re-point before the sprint commits to it.',
    detectedAgo: '1d ago',
    evidence: [
      {
        platform: 'jira',
        ref: 'MOB-410',
        url: `${JIRA}/MOB-410`,
        quote:
          'Offline background-sync. Estimate: 3 points. Committed to the current sprint.',
        highlight: 'Estimate: 3 points',
        attribution: 'planned this sprint · Diego Ramos',
      },
    ],
    actionPlan: [
      {
        id: 're-point-from-history',
        platform: 'jira',
        title: 'Re-point MOB-410 to match its history and split the work',
        detail:
          'Bump MOB-410 to 13 points and split the retry and conflict handling into its own ticket before the sprint locks.',
        actionLabel: 'Re-point',
        appliedLabel: 'Re-pointed and split',
      },
    ],
  },

  // ─── Playbook · team not looped in · cross-platform (Jira + Confluence) ───
  {
    id: 'arch-decision-not-propagated',
    category: 'playbook',
    playbookType: 'team-not-looped-in',
    severity: 'medium',
    playbook:
      'When an ADR changes a shared queue or infra choice, every team with a consumer ticket must be tagged on the decision.',
    team: 'Risk Platform',
    title:
      'An approved architecture decision never reached the team building against it',
    summary:
      'The model-retraining ADR switched the queue to Kafka, but the consumer ticket is still building on SQS.',
    detail:
      'A signed-off ADR moved the retraining pipeline from SQS to Kafka. The team implementing the consumer never saw it - their ticket still references the SQS client, so they are building the wrong integration.',
    detectedAgo: '1d ago',
    evidence: [
      {
        platform: 'confluence',
        ref: 'ADR-31: Queue choice',
        url: `${CONFLUENCE}/661120/ADR-31-Queue`,
        quote:
          'Decision: migrate the retraining pipeline from SQS to Kafka. Status: Accepted.',
        highlight: 'migrate the retraining pipeline from SQS to Kafka',
        attribution: 'Accepted · 5 days ago',
      },
      {
        platform: 'jira',
        ref: 'RISK-664',
        url: `${JIRA}/RISK-664`,
        quote: 'Build retraining consumer against the SQS queue client.',
        highlight: 'against the SQS queue client',
        attribution: 'no reference to ADR-31',
      },
    ],
    actionPlan: [
      {
        id: 'update-ticket',
        platform: 'jira',
        title: 'Update the consumer ticket to Kafka and link the ADR',
        detail: 'Edit RISK-664 to reference ADR-31 and the Kafka client.',
        actionLabel: 'Update ticket',
        appliedLabel: 'Ticket updated',
      },
      {
        id: 'brief-team',
        platform: 'slack',
        title: 'Brief the Risk Platform team on the decision',
        detail:
          'Share ADR-31 in #risk-platform so the change is visible to everyone.',
        actionLabel: 'Share ADR',
        appliedLabel: 'Team briefed',
      },
    ],
  },
];

export function getEngineeringBreakById(
  breakId: string
): EngineeringBreak | undefined {
  return ENGINEERING_BREAKS.find(
    (engineeringBreak) => engineeringBreak.id === breakId
  );
}
